import React, { useState, useMemo, useEffect } from 'react';
import { MatrixGraduationCap, MatrixSearch, MatrixChart, MatrixMoney, MatrixLocation, MatrixStar, MatrixCheck, MatrixDocument } from '../components/MatrixEmojis';
import { formatRubWithLiveInr, getExchangeRateInfo } from '../utils/currencyUtils';
import { universities as universitiesData, getUniversityWhyChooseData } from '../data/universities';

const UniversityFeesPage = ({ darkMode, setHideHeader = () => {} }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedUniversity, setSelectedUniversity] = useState(null);
  const [sortOption, setSortOption] = useState('alphabetical'); // 'alphabetical', 'numerical', 'lowToHigh', 'highToLow', 'whoNmcEnglish'
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [imageLoadingStates, setImageLoadingStates] = useState({});
  const [liveExchangeRates, setLiveExchangeRates] = useState({});
  const [exchangeRateLoading, setExchangeRateLoading] = useState(false);
  // Touch/swipe state
  const [touchStart, setTouchStart] = useState(null);
  const [touchEnd, setTouchEnd] = useState(null);
  const [mouseStart, setMouseStart] = useState(null);
  const [mouseEnd, setMouseEnd] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [showSortOptions, setShowSortOptions] = useState(false);
  const [swipeDirection, setSwipeDirection] = useState(null);
  const [touchStartTime, setTouchStartTime] = useState(null);
  const [showWhyChoose, setShowWhyChoose] = useState(false);

  // Minimum distance for a swipe (reduced for better touch sensitivity)
  const minSwipeDistance = 30;
  const maxSwipeTime = 300; // Maximum time for a swipe (in ms) for momentum detection

  // Load live exchange rates on component mount
  useEffect(() => {
    const loadExchangeRates = async () => {
      setExchangeRateLoading(true);
      try {
        const rateInfo = await getExchangeRateInfo();
        setLiveExchangeRates(rateInfo);
      } catch (error) {
        console.warn('Failed to load exchange rates:', error);
        // Set fallback rates
        setLiveExchangeRates({
          rubToInr: '1.10',
          usdToInr: '83.50',
          lastUpdated: 'Using fallback rates',
          isLive: false
        });
      } finally {
        setExchangeRateLoading(false);
      }
    };

    loadExchangeRates();
  }, []);

  // Content Protection - Prevent copying, right-click, and developer tools
  useEffect(() => {
    // Disable right-click context menu
    const handleContextMenu = (e) => {
      e.preventDefault();
      return false;
    };

    // Disable text selection
    const handleSelectStart = (e) => {
      e.preventDefault();
      return false;
    };

    // Disable drag and drop
    const handleDragStart = (e) => {
      e.preventDefault();
      return false;
    };

    // Disable keyboard shortcuts for developer tools and copying
    const handleKeyDown = (e) => {
      // Disable F12 (Developer Tools)
      if (e.keyCode === 123) {
        e.preventDefault();
        return false;
      }
      
      // Disable Ctrl+Shift+I (Developer Tools)
      if (e.ctrlKey && e.shiftKey && e.keyCode === 73) {
        e.preventDefault();
        return false;
      }
      
      // Disable Ctrl+Shift+C (Inspect Element)
      if (e.ctrlKey && e.shiftKey && e.keyCode === 67) {
        e.preventDefault();
        return false;
      }
      
      // Disable Ctrl+Shift+J (Console)
      if (e.ctrlKey && e.shiftKey && e.keyCode === 74) {
        e.preventDefault();
        return false;
      }
      
      // Disable Ctrl+U (View Source)
      if (e.ctrlKey && e.keyCode === 85) {
        e.preventDefault();
        return false;
      }
      
      // Disable Ctrl+A (Select All)
      if (e.ctrlKey && e.keyCode === 65) {
        e.preventDefault();
        return false;
      }
      
      // Disable Ctrl+C (Copy)
      if (e.ctrlKey && e.keyCode === 67) {
        e.preventDefault();
        return false;
      }
      
      // Disable Ctrl+V (Paste)
      if (e.ctrlKey && e.keyCode === 86) {
        e.preventDefault();
        return false;
      }
      
      // Disable Ctrl+X (Cut)
      if (e.ctrlKey && e.keyCode === 88) {
        e.preventDefault();
        return false;
      }
      
      // Disable Ctrl+S (Save)
      if (e.ctrlKey && e.keyCode === 83) {
        e.preventDefault();
        return false;
      }
      
      // Disable Ctrl+P (Print)
      if (e.ctrlKey && e.keyCode === 80) {
        e.preventDefault();
        return false;
      }
    };

    // Add event listeners
    document.addEventListener('contextmenu', handleContextMenu);
    document.addEventListener('selectstart', handleSelectStart);
    document.addEventListener('dragstart', handleDragStart);
    document.addEventListener('keydown', handleKeyDown);

    // Disable text selection via CSS
    document.body.style.userSelect = 'none';
    document.body.style.webkitUserSelect = 'none';
    document.body.style.mozUserSelect = 'none';
    document.body.style.msUserSelect = 'none';

    // Disable drag and drop via CSS
    document.body.style.webkitUserDrag = 'none';
    document.body.style.mozUserDrag = 'none';

    // Clear console periodically to prevent inspection
    const clearConsole = () => {
      try {
        console.clear();
        console.log('%cSTOP!', 'color: red; font-size: 50px; font-weight: bold;');
        console.log('%cThis is a browser feature intended for developers. Content is protected.', 'color: red; font-size: 16px;');
      } catch (e) {
        // Ignore errors
      }
    };

    // Clear console every 3 seconds
    const consoleInterval = setInterval(clearConsole, 3000);
    clearConsole(); // Clear immediately

    // Detect if developer tools are open
    let devtools = { open: false, orientation: null };
    
    setInterval(() => {
      if (window.outerHeight - window.innerHeight > 160 || window.outerWidth - window.innerWidth > 160) {
        if (!devtools.open) {
          devtools.open = true;
          // Redirect or show warning when dev tools are detected
          document.body.innerHTML = '<div style="display: flex; justify-content: center; align-items: center; height: 100vh; font-family: Arial; font-size: 24px; color: red; text-align: center; background: #000;">⚠️<br/>Content Protected<br/>Please close developer tools</div>';
        }
      } else {
        if (devtools.open) {
          devtools.open = false;
          // Reload page when dev tools are closed
          window.location.reload();
        }
      }
    }, 500);

    // Cleanup function
    return () => {
      document.removeEventListener('contextmenu', handleContextMenu);
      document.removeEventListener('selectstart', handleSelectStart);
      document.removeEventListener('dragstart', handleDragStart);
      document.removeEventListener('keydown', handleKeyDown);
      clearInterval(consoleInterval);
      
      // Reset styles
      document.body.style.userSelect = '';
      document.body.style.webkitUserSelect = '';
      document.body.style.mozUserSelect = '';
      document.body.style.msUserSelect = '';
      document.body.style.webkitUserDrag = '';
      document.body.style.mozUserDrag = '';
    };
  }, []);

  // Disable print screen functionality
  useEffect(() => {
    const handleKeyUp = (e) => {
      // Detect Print Screen key
      if (e.keyCode === 44) {
        navigator.clipboard.writeText('').catch(() => {});
        alert('Screenshots are not allowed on this page.');
      }
    };

    document.addEventListener('keyup', handleKeyUp);
    
    return () => {
      document.removeEventListener('keyup', handleKeyUp);
    };
  }, []);

  // Hide/show header based on popup state
  useEffect(() => {
    if (setHideHeader && typeof setHideHeader === 'function') {
      setHideHeader(!!selectedUniversity);
    }
  }, [selectedUniversity, setHideHeader]);

  // Cleanup: Show header when component unmounts
  useEffect(() => {
    return () => {
      if (setHideHeader && typeof setHideHeader === 'function') {
        setHideHeader(false);
      }
    };
  }, [setHideHeader]);

  // Handle touch start
  const onTouchStart = (e) => {
    setTouchEnd(null); // Otherwise the swipe is fired even with usual touch events
    setTouchStart(e.targetTouches[0].clientX);
    setTouchStartTime(Date.now());
    setSwipeDirection(null);
    // Store initial Y position for vertical scroll detection
    const touchStartY = e.targetTouches[0].clientY;
    e.currentTarget.touchStartY = touchStartY;
    // Don't prevent default here - let vertical scrolling work
  };

  // Handle touch move
  const onTouchMove = (e) => {
    const currentTouch = e.targetTouches[0].clientX;
    const currentTouchY = e.targetTouches[0].clientY;
    setTouchEnd(currentTouch);
    
    // Determine swipe direction for visual feedback
    if (touchStart && currentTouch) {
      const distanceX = touchStart - currentTouch;
      const distanceY = (e.currentTarget.touchStartY || currentTouchY) - currentTouchY;
      
      // Only prevent default if horizontal movement is greater than vertical
      // This allows vertical scrolling while enabling horizontal swiping
      if (Math.abs(distanceX) > Math.abs(distanceY) && Math.abs(distanceX) > 10) {
        setSwipeDirection(distanceX > 0 ? 'left' : 'right');
        // Only prevent default for horizontal swipes
        e.preventDefault();
      }
    }
  };

  // Handle touch end - determine if it was a swipe
  const onTouchEnd = (images) => {
    if (!touchStart || !touchEnd || isTransitioning) return;
    
    const distance = touchStart - touchEnd;
    const swipeTime = Date.now() - touchStartTime;
    const isLeftSwipe = distance > minSwipeDistance;
    const isRightSwipe = distance < -minSwipeDistance;
    
    // Add momentum detection - faster swipes require less distance
    const isFastSwipe = swipeTime < maxSwipeTime;
    const momentumMinDistance = isFastSwipe ? minSwipeDistance * 0.6 : minSwipeDistance;
    
    const isLeftMomentumSwipe = distance > momentumMinDistance;
    const isRightMomentumSwipe = distance < -momentumMinDistance;

    if (isLeftSwipe || isRightSwipe || isLeftMomentumSwipe || isRightMomentumSwipe) {
      setIsTransitioning(true);
      
      // Calculate new index without looping - stop at boundaries
      let newIndex = currentImageIndex;
      if (isLeftSwipe || isLeftMomentumSwipe) {
        // Swipe left - go to next image (if not at last)
        if (currentImageIndex < images.gallery.length - 1) {
          newIndex = currentImageIndex + 1;
        }
      } else {
        // Swipe right - go to previous image (if not at first)
        if (currentImageIndex > 0) {
          newIndex = currentImageIndex - 1;
        }
      }
      
      setCurrentImageIndex(newIndex);
      
      // Reset transition flag faster for smoother response
      setTimeout(() => setIsTransitioning(false), 50);
    }
    
    // Reset swipe states
    setSwipeDirection(null);
    setTouchStart(null);
    setTouchEnd(null);
    setTouchStartTime(null);
  };

  // Handle mouse events for desktop drag
  const onMouseDown = (e) => {
    setIsDragging(true);
    setMouseEnd(null);
    setMouseStart(e.clientX);
    setTouchStartTime(Date.now());
    setSwipeDirection(null);
    // Prevent text selection during drag
    e.preventDefault();
  };

  const onMouseMove = (e) => {
    if (!isDragging) return;
    const currentMouse = e.clientX;
    setMouseEnd(currentMouse);
    
    // Determine swipe direction for visual feedback
    if (mouseStart && currentMouse) {
      const distance = mouseStart - currentMouse;
      if (Math.abs(distance) > 10) { // Minimum distance for direction detection
        setSwipeDirection(distance > 0 ? 'left' : 'right');
      }
    }
    
    // Prevent text selection during drag
    e.preventDefault();
  };

  const onMouseUp = (images) => {
    if (!isDragging || !mouseStart || !mouseEnd || isTransitioning) {
      setIsDragging(false);
      setSwipeDirection(null);
      return;
    }
    
    const distance = mouseStart - mouseEnd;
    const swipeTime = Date.now() - touchStartTime;
    const isLeftDrag = distance > minSwipeDistance;
    const isRightDrag = distance < -minSwipeDistance;
    
    // Add momentum detection - faster drags require less distance
    const isFastDrag = swipeTime < maxSwipeTime;
    const momentumMinDistance = isFastDrag ? minSwipeDistance * 0.6 : minSwipeDistance;
    
    const isLeftMomentumDrag = distance > momentumMinDistance;
    const isRightMomentumDrag = distance < -momentumMinDistance;

    if (isLeftDrag || isRightDrag || isLeftMomentumDrag || isRightMomentumDrag) {
      setIsTransitioning(true);
      
      // Calculate new index without looping - stop at boundaries
      let newIndex = currentImageIndex;
      if (isLeftDrag || isLeftMomentumDrag) {
        // Drag left - go to next image (if not at last)
        if (currentImageIndex < images.gallery.length - 1) {
          newIndex = currentImageIndex + 1;
        }
      } else {
        // Drag right - go to previous image (if not at first)
        if (currentImageIndex > 0) {
          newIndex = currentImageIndex - 1;
        }
      }
      
      setCurrentImageIndex(newIndex);
      
      // Reset transition flag faster for smoother response
      setTimeout(() => setIsTransitioning(false), 50);
    }
    
    setIsDragging(false);
    setSwipeDirection(null);
  };

  const onMouseLeave = () => {
    setIsDragging(false);
    setSwipeDirection(null);
  };

  // Function to get university images
  const getUniversityImages = (universityName) => {
    try {
      // Create the base path for the university folder (served from public directory)
      const basePath = `/College Images/${universityName}`;
      
      let profile = `${basePath}/profile.webp`;
      let gallery = [];
      
      // Handle different universities based on their actual image structure
      switch (universityName) {
        case 'Amur State University':
          profile = `${basePath}/profile.webp`;
          gallery = [
            `${basePath}/unnamed (1).webp`,
            `${basePath}/unnamed (2).webp`,
            `${basePath}/unnamed (3).webp`,
            `${basePath}/unnamed (4).webp`,
            `${basePath}/unnamed (5).webp`
          ];
          break;
            case 'Astrakhan State Medical University':
          profile = `${basePath}/profile.webp`;
          gallery = [
            `${basePath}/unnamed (1).webp`,
            `${basePath}/unnamed (1).jpg`,
            `${basePath}/unnamed (5).webp`,
            `${basePath}/unnamed.webp`,
            `${basePath}/unnamed.jpg`
          ];
          break;
          
        case 'Bashkir State Medical University':
          profile = `${basePath}/profile.webp`;
          gallery = [
            `${basePath}/unnamed (1).webp`,
            `${basePath}/unnamed (2).webp`,
            `${basePath}/unnamed (3).webp`,
            `${basePath}/unnamed (4).webp`,
            `${basePath}/unnamed.webp`
          ];
          break;
          
        case 'Chechen State University':
          profile = `${basePath}/profile.webp`;
          gallery = [
            `${basePath}/unnamed (1).webp`,
            `${basePath}/unnamed (2).webp`,
            `${basePath}/unnamed (3).webp`,
            `${basePath}/unnamed (4).webp`,
            `${basePath}/unnamed.webp`
          ];
          break;
          
        case 'Chuvash State Medical University':
          profile = `${basePath}/profile.webp`;
          gallery = [
            `${basePath}/unnamed (1).webp`,
            `${basePath}/unnamed (2).webp`,
            `${basePath}/unnamed (3).webp`,
            `${basePath}/unnamed (4).webp`,
            `${basePath}/unnamed.webp`
          ];
          break;
          
        case 'Crimean Federal University':
          profile = `${basePath}/profile.webp`;
          gallery = [
            `${basePath}/unnamed (1).webp`,
            `${basePath}/unnamed (2).webp`,
            `${basePath}/unnamed (3).webp`,
            `${basePath}/unnamed (4).webp`,
            `${basePath}/unnamed.webp`
          ];
          break;
          
        case 'Far Eastern Federal University':
          profile = `${basePath}/profile.jpg`;
          gallery = [
            `${basePath}/1579003945php2aKcco.jpeg`,
            `${basePath}/1579003945php9NzXGf.jpeg`,
            `${basePath}/1579003945phpFb4TbD.jpeg`,
            `${basePath}/Far_Eastern_Federal_University_Medical_Center.jpg`,
            `${basePath}/p3n-cY7Skyg.jpg`
          ];
          break;
            case 'I.M. Sechenov First Moscow State Medical':
          profile = `${basePath}/profile.webp`;
          gallery = [
            `${basePath}/unnamed (1).webp`,
            `${basePath}/unnamed (2).webp`,
            `${basePath}/unnamed (3).webp`,
            `${basePath}/unnamed (4).webp`,
            `${basePath}/unnamed.webp`
          ];
          break;
          
        case 'Immanuel Kant Baltic Federal University':
          profile = `${basePath}/profile.webp`;
          gallery = [
            `${basePath}/DJI_0054.webp`,
            `${basePath}/unnamed (1).webp`,
            `${basePath}/unnamed (2).webp`,
            `${basePath}/unnamed.webp`,
            `${basePath}/_S2A5479.webp`
          ];
          break;
          
        case 'Ivanovo State Medical University':
          profile = `${basePath}/profile.webp`;
          gallery = [
            `${basePath}/unnamed (1).webp`,
            `${basePath}/unnamed (2).webp`,
            `${basePath}/unnamed (3).webp`,
            `${basePath}/unnamed (4).webp`,
            `${basePath}/unnamed.webp`
          ];
          break;
          
        case 'Kabardino Balkarian State University':
          profile = `${basePath}/profile.webp`;
          gallery = [
            `${basePath}/1092016 ДЕНЬ ЗНАНИЙ (2).webp`,
            `${basePath}/19042016 ЯРМАРКА ОБРАЗОВАТЕЛЬНЫХ УСЛУГ (2).webp`,
            `${basePath}/IMG_2103.webp`,
            `${basePath}/IMG_2443.webp`,
            `${basePath}/НОВОГОДНИЙ БАЛ.webp`
          ];
          break;
          
        case 'Kemerovo State Medical University':
          profile = `${basePath}/Kemerovo-State-Medical-University-Russia.webp`;
          gallery = [
            `${basePath}/1.jpg`,
            `${basePath}/14656125681712044835.webp`,
            `${basePath}/2017-08-30-1536x1152-1-924x462.jpg`,
            `${basePath}/64341046_685297858597036_2270841680041082880_n-924x462.jpg`,
            `${basePath}/682cc251421c0-1747763793.jpg`,
            `${basePath}/images.jpg`
          ];
          break;
          
        case 'Lobachevsky National Research Medical University':
          profile = `${basePath}/profile.jpg`;
          gallery = [
            `${basePath}/building-768-e788449bbdbb9022fd9bab003dbb66ac.jpeg`,
            `${basePath}/LOBACHEVSKY-STATE-MEDICAL-UNIVERSITY.webp`,
            `${basePath}/WhatsApp-Image-2022-02-16-at-22.36.08-1024x682.jpeg`,
            `${basePath}/WhatsApp-Image-2022-02-16-at-22.36.09-1024x682.jpeg`,
            `${basePath}/WhatsApp-Image-2022-02-16-at-22.36.10-1024x768.jpeg`
          ];          break;
          
        case 'MEPHI National Nuclear Research University (Moscow)':
          profile = `${basePath}/profile.webp`;
          gallery = [
            `${basePath}/DSC_1193.webp`,
            `${basePath}/unnamed (1).webp`,
            `${basePath}/unnamed (2).webp`,
            `${basePath}/unnamed (3).webp`,
            `${basePath}/unnamed.webp`
          ];
          break;
          
        case 'MEPHI National Nuclear Research University (Obninsk)':
          profile = `${basePath}/profile.webp`;
          gallery = [
            `${basePath}/unnamed (1).webp`,
            `${basePath}/unnamed (2).webp`,
            `${basePath}/unnamed (3).webp`,
            `${basePath}/unnamed (4).webp`,
            `${basePath}/unnamed.webp`
          ];
          break;
          
        case 'North Caucasian State Medical Academy':
          profile = `${basePath}/profile.jpeg`;
          gallery = [
            `${basePath}/1733301748phpSgfDqm.jpeg`,
            `${basePath}/images (1).jpg`,
            `${basePath}/images.jpg`,
            `${basePath}/ncsa.png`,
            `${basePath}/North Caucasian State Medical Academy.jpeg`
          ];
          break;
          
        case 'North-Western State Medical University':
          profile = `${basePath}/profile.jpg`;
          gallery = [
            `${basePath}/images (2).jpg`,
            `${basePath}/images.jpg`,
            `${basePath}/north-western-state-medical-university-img.webp`,
            `${basePath}/north-western-state-medical-university.webp`,
            `${basePath}/NWSMU-1024x672.webp`
          ];
          break;
          
        case 'Omsk State Medical University':
          profile = `${basePath}/profile.webp`;
          gallery = [
            `${basePath}/cr9.webp`,
            `${basePath}/images.jpg`,
            `${basePath}/omsk-1024x538.png`,
            `${basePath}/omsk-state-medical-university-hospital-training.jpg`,
            `${basePath}/OMSK-State-Medical-University.webp`
          ];
          break;
          
        case 'Privolzhsky Research Medical University':
          profile = `${basePath}/profile.webp`;
          gallery = [
            `${basePath}/unnamed (1).webp`,
            `${basePath}/unnamed (2).webp`,
            `${basePath}/unnamed (3).webp`,
            `${basePath}/unnamed (4).webp`,
            `${basePath}/unnamed.webp`
          ];
          break;
          
        case 'Rostov State Medical University':
          profile = `${basePath}/profile.jpeg`;
          gallery = [
            `${basePath}/8.jpg`,
            `${basePath}/Rostov-State-Medical-University-Russia.webp`,
            `${basePath}/rostov-state-medical-university.webp`,
            `${basePath}/unnamed (5).webp`,
            `${basePath}/unnamed.webp`
          ];
          break;
          
        case 'Saint Petersburg State University of Veterinary Medicine':
          profile = `${basePath}/profile.jpg`;
          gallery = [
            `${basePath}/2tri7gpewi4tf9kzzex6zwpm3s2av3w6.webp`,
            `${basePath}/enan3jsgkxnx67qdtjh32lts3i9g6b2b.webp`,
            `${basePath}/eu716cmlq4mfwl9o3lj5d3l6gdhxol5o.webp`,
            `${basePath}/images (1).jpg`,
            `${basePath}/images.jpg`,
            `${basePath}/oq84z60uwquzaexi3ieu7z35efqq715u.webp`,
            `${basePath}/q7sxtujugcenp1yjry5cpma1bf5j8iao.webp`,
            `${basePath}/ux6d7u8pgz7zkrblmxh03ihbezeqpwnf.webp`
          ];
          break;
          
        case 'Sevastopol State University':
          profile = `${basePath}/profile.jpg`;
          gallery = [
            `${basePath}/2wdfaxnrs4lk18plgmhgn0ynfwwpvxu4.jpg`,
            `${basePath}/660_464_smapse-sevastopol-skij-gosudarstvennyj-universitet-2.jpg`,
            `${basePath}/images (1).jpg`,
            `${basePath}/images (2).jpg`,
            `${basePath}/images.jpg`
          ];
          break;
            case 'Siberian State Medical University':
          profile = `${basePath}/profile.jpg`;
          gallery = [
            `${basePath}/1-37.jpg`,
            `${basePath}/1658572954.jpg`,
            `${basePath}/Campus_V3C8EUZtY.webp`,
            `${basePath}/images (1).jpg`,
            `${basePath}/images (2).jpg`,
            `${basePath}/images.jpg`,
            `${basePath}/Siberian-State-Medical-University9.png`
          ];
          break;
          
        case 'Synergy University (Moscow)':
          profile = `${basePath}/profile.webp`;
          gallery = [
            `${basePath}/2024-11-29.webp`,
            `${basePath}/unnamed (1).webp`,
            `${basePath}/unnamed (2).webp`,
            `${basePath}/unnamed (3).webp`,
            `${basePath}/unnamed.webp`
          ];
          break;
          
        case 'Tambov State University':
          profile = `${basePath}/profile.webp`;
          gallery = [
            `${basePath}/1595933991phpued4RS.jpeg`,
            `${basePath}/825_585_smapse-tambovskij-gosudarstvennyj-universitet-imeni-g-r-derzavina-1.jpg`,
            `${basePath}/Cover-image_YluTiVLXI.webp`,
            `${basePath}/images (1).jpg`,
            `${basePath}/images-2.jpg`,
            `${basePath}/images.jpg`
          ];
          break;
          
        case 'Tver State Medical University':
          profile = `${basePath}/profile.jpg`;
          gallery = [
            `${basePath}/homebanner26.webp`,
            `${basePath}/images.jpg`,
            `${basePath}/L_height.webp`,
            `${basePath}/Tver-State-Medical-University-scaled.jpg`,
            `${basePath}/Tver-State-Medical-University.webp`
          ];
          break;
            case 'Ulyanovsk State University':
          profile = `${basePath}/profile.jpg`;
          gallery = [
            `${basePath}/hostel-picture-1527672115.jpg`,
            `${basePath}/images (1).jpg`,
            `${basePath}/images.jpg`,
            `${basePath}/ul1.jpg`,
            `${basePath}/Ulyanovsk-Medicine-Ecology-01.webp`,
            `${basePath}/ulyanovsk-state-university.jpg`
          ];
          break;
          
        case 'Volgograd State Medical University':
          profile = `${basePath}/profile.webp`;
          gallery = [
            `${basePath}/hostel-room-russia.jpg`,
            `${basePath}/images (1).jpg`,
            `${basePath}/images.jpg`,
            `${basePath}/The_Volgograd_State_Medical_University_001.jpg`,
            `${basePath}/Volgograd-State-University9.png`
          ];
          break;
            case 'Voronezh State Medical University':
          profile = `${basePath}/profile.jpg`;
          gallery = [
            `${basePath}/bannerimage.jpg`,
            `${basePath}/course-single23.jpg`,
            `${basePath}/Voronezh-Medical-university-Russia.jpg`,
            `${basePath}/voronezh-state-medical-university-web.jpg`,
            `${basePath}/VORONEZH-STATE-MEDICAL-UNIVERSITY.webp`,
            `${basePath}/ВГМУ_(ВГМИ)_-1.jpg`
          ];
          break;
          
        default:
          // Default fallback for other universities
          profile = `${basePath}/profile.webp`;
          gallery = [
            `${basePath}/unnamed (1).webp`,
            `${basePath}/unnamed (2).webp`,
            `${basePath}/unnamed (3).webp`,
            `${basePath}/unnamed (4).webp`,
            `${basePath}/unnamed (5).webp`
          ];
      }
      
      const imageData = { profile, gallery };
      // Removed excessive console.log to prevent infinite loop spam
      return imageData;
    } catch (error) {
      console.warn('No images found for', universityName, '- Error:', error);
      return null;
    }
  };
  
  // Enhanced currency conversion with correct exchange rate
  const convertRubToInr = (feeString) => {
    // Handle different fee formats
    if (feeString.includes('₹')) {
      // Already in INR, just return the first number if it's a range
      let amount = feeString.replace(/[₹,]/g, '');
      if (amount.includes('-')) {
        amount = amount.split('-')[0];
      }
      const numericValue = parseFloat(amount);
      if (isNaN(numericValue)) return '₹0';
      return `₹${numericValue.toLocaleString('en-IN')}`;
    }
    
    if (feeString.includes('USD')) {
      // Convert USD to INR (approximate rate: 1 USD = 83 INR)
      let amount = feeString.replace(/[USD$,]/g, '');
      if (amount.includes('-')) {
        amount = amount.split('-')[0];
      }
      const numericValue = parseFloat(amount);
      if (isNaN(numericValue)) return '₹0';
      const inrValue = Math.round(numericValue * 83);
      return `₹${inrValue.toLocaleString('en-IN')}`;
    }
    
    // Handle RUB amounts
    let amount = feeString.replace(/[RUB,]/g, '');
    if (amount.includes('-')) {
      amount = amount.split('-')[0]; // Take the lower range
    }
    
    const numericValue = parseFloat(amount);
    if (isNaN(numericValue)) return '₹0';
    
    const inrValue = Math.round(numericValue * 1.10); // Using 1.10 INR/RUB rate
    return `₹${inrValue.toLocaleString('en-IN')}`;
  };
  
  // For card display - only show INR amounts (no RUB)
  const formatFeeForCard = (feeString) => {
    const rubToInrRate = parseFloat(liveExchangeRates.rubToInr) || 1.10;
    
    if (feeString.includes('RUB')) {
      // For RUB amounts, only show converted INR equivalent
      if (feeString.includes('-')) {
        // Handle ranges like "80,000-100,000 RUB"
        const parts = feeString.replace(' RUB', '').split('-');
        const lowerAmount = parseFloat(parts[0].replace(/,/g, ''));
        const upperAmount = parseFloat(parts[1].replace(/,/g, ''));
        
        if (!isNaN(lowerAmount) && !isNaN(upperAmount)) {
          const lowerINR = Math.round(lowerAmount * rubToInrRate);
          const upperINR = Math.round(upperAmount * rubToInrRate);
          return `₹${lowerINR.toLocaleString('en-IN')}-₹${upperINR.toLocaleString('en-IN')}`;
        }
      } else {
        // Single RUB amount
        const numericValue = parseFloat(feeString.replace(/[^\d]/g, ''));
        if (!isNaN(numericValue)) {
          const inrValue = Math.round(numericValue * rubToInrRate);
          return `₹${inrValue.toLocaleString('en-IN')}`;
        }
      }
    } else if (feeString.includes('USD')) {
      const usdToInrRate = parseFloat(liveExchangeRates.usdToInr) || 83.50;
      const numericValue = parseFloat(feeString.replace(/[^\d]/g, ''));
      if (!isNaN(numericValue)) {
        const inrValue = Math.round(numericValue * usdToInrRate);
        return `₹${inrValue.toLocaleString('en-IN')}`;
      }
    }
    return feeString;
  };

  // For modal display - show both RUB and INR
  const formatFeeWithInr = (feeString) => {
    const rubToInrRate = parseFloat(liveExchangeRates.rubToInr) || 1.10;
    
    if (feeString.includes('RUB')) {
      // For RUB amounts, show the range with converted INR equivalent
      if (feeString.includes('-')) {
        // Handle ranges like "80,000-100,000 RUB"
        const parts = feeString.replace(' RUB', '').split('-');
        const lowerAmount = parseFloat(parts[0].replace(/,/g, ''));
        const upperAmount = parseFloat(parts[1].replace(/,/g, ''));
        
        if (!isNaN(lowerAmount) && !isNaN(upperAmount)) {
          const lowerINR = Math.round(lowerAmount * rubToInrRate);
          const upperINR = Math.round(upperAmount * rubToInrRate);
          return `${feeString} (₹${lowerINR.toLocaleString('en-IN')}-₹${upperINR.toLocaleString('en-IN')})`;
        }
      } else {
        // Single RUB amount
        const numericValue = parseFloat(feeString.replace(/[^\d]/g, ''));
        if (!isNaN(numericValue)) {
          const inrValue = Math.round(numericValue * rubToInrRate);
          return `${feeString} (₹${inrValue.toLocaleString('en-IN')})`;
        }
      }
    } else if (feeString.includes('USD')) {
      const usdToInrRate = parseFloat(liveExchangeRates.usdToInr) || 83.50;
      const numericValue = parseFloat(feeString.replace(/[^\d]/g, ''));
      if (!isNaN(numericValue)) {
        const inrValue = Math.round(numericValue * usdToInrRate);
        return `${feeString} (₹${inrValue.toLocaleString('en-IN')})`;
      }
    }
    return feeString;
  };

  // Calculate complete 1st year total with ALL expenses - Flight tickets NOT converted to RUB (purchased in India)
  const calculateTotalFirstYear = (university) => {
    // Extract numeric values - handle ranges by taking the first number
    const tuition = parseFloat(university.tuition.replace(/[^\d]/g, '')) || 0;
      // Handle different hostel fee formats
    let hostelRUB = 0;
    if (university.hostel.includes('₹')) {
      // Convert INR to RUB for calculation using live rate
      let amount = university.hostel.replace(/[₹,]/g, '');
      if (amount.includes('-')) {
        amount = amount.split('-')[0];
      }
      const inrAmount = parseFloat(amount);
      if (!isNaN(inrAmount)) {
        const rubToInrRate = parseFloat(liveExchangeRates.rubToInr) || 1.10;
        hostelRUB = Math.round(inrAmount / rubToInrRate);
      }
    } else if (university.hostel.includes('USD')) {
      // Convert USD to RUB for calculation (1 USD ≈ 91 RUB)
      let amount = university.hostel.replace(/[USD$,]/g, '');
      if (amount.includes('-')) {
        amount = amount.split('-')[0];
      }
      const usdAmount = parseFloat(amount);
      if (!isNaN(usdAmount)) {
        hostelRUB = Math.round(usdAmount * 91);
      }
    } else {
      // RUB amount
      let amount = university.hostel.replace(/[RUB,]/g, '');
      if (amount.includes('-')) {
        amount = amount.split('-')[0];
      }
      const rubAmount = parseFloat(amount);
      if (!isNaN(rubAmount)) {
        hostelRUB = rubAmount;
      }
    }
    
    const medical = 8000;
    const insurance = 7500;
    const documentation = 21000;
    const administrativeUSD = 700;
    const flightTicketINR = 65000; // Flight tickets purchased in India (INR only)
    
    // Convert USD to RUB (using approximate rate 1 USD = 91 RUB)
    const administrativeRUB = administrativeUSD * 91;
    
    // Total RUB amount (only Russia-based fees, NO flight tickets)
    const totalRUB = tuition + hostelRUB + medical + insurance + documentation + administrativeRUB;
    
    // Convert RUB total to INR and add flight tickets
    const rubToInrRate = parseFloat(liveExchangeRates.rubToInr) || 1.10;
    const totalRUBInINR = Math.round(totalRUB * rubToInrRate);
    const totalINR = totalRUBInINR + flightTicketINR;
    
    // Convert to USD  
    const totalUSD = Math.round(totalRUB / 91);
    
    // Calculate flight ticket RUB for display purposes (not included in RUB total)
    const flightTicketRUB = Math.round(flightTicketINR / rubToInrRate);
      
    return {
      totalRUB: (totalRUB || 0).toLocaleString('en-IN'),
      totalINR: `₹${(totalINR || 0).toLocaleString('en-IN')}`,
      totalUSD: `$${(totalUSD || 0).toLocaleString('en-IN')}`,
      breakdown: {
        tuition: tuition || 0,
        hostelRUB: hostelRUB || 0,
        medical: medical || 0,
        insurance: insurance || 0,
        documentation: documentation || 0,
        administrativeRUB: administrativeRUB || 0,
        flightTicketINR: flightTicketINR || 0,
        flightTicketRUB: flightTicketRUB || 0,
        totalRUB: totalRUB || 0,
        totalINR: totalINR || 0
      }
    };
  };
  // University Number Icons (1-29)
  const UniversityIcons = {
    amur: () => (
      <div className="w-12 h-12 rounded-full bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center text-white font-bold text-lg shadow-lg">
        1
      </div>
    ),
    astrakhan: () => (
      <div className="w-12 h-12 rounded-full bg-gradient-to-br from-blue-500 to-cyan-600 flex items-center justify-center text-white font-bold text-lg shadow-lg">
        2
      </div>
    ),
    bashkir: () => (
      <div className="w-12 h-12 rounded-full bg-gradient-to-br from-green-500 to-emerald-600 flex items-center justify-center text-white font-bold text-lg shadow-lg">
        3
      </div>
    ),
    chechen: () => (
      <div className="w-12 h-12 rounded-full bg-gradient-to-br from-red-500 to-pink-600 flex items-center justify-center text-white font-bold text-lg shadow-lg">
        4
      </div>
    ),
    chuvash: () => (
      <div className="w-12 h-12 rounded-full bg-gradient-to-br from-yellow-500 to-orange-600 flex items-center justify-center text-white font-bold text-lg shadow-lg">
        5
      </div>
    ),
    crimean: () => (
      <div className="w-12 h-12 rounded-full bg-gradient-to-br from-purple-500 to-violet-600 flex items-center justify-center text-white font-bold text-lg shadow-lg">
        6
      </div>
    ),
    fareastern: () => (
      <div className="w-12 h-12 rounded-full bg-gradient-to-br from-teal-500 to-cyan-600 flex items-center justify-center text-white font-bold text-lg shadow-lg">
        7
      </div>
    ),
    sechenov: () => (
      <div className="w-12 h-12 rounded-full bg-gradient-to-br from-amber-500 to-yellow-600 flex items-center justify-center text-white font-bold text-lg shadow-lg">
        8
      </div>
    ),
    kant: () => (
      <div className="w-12 h-12 rounded-full bg-gradient-to-br from-rose-500 to-red-600 flex items-center justify-center text-white font-bold text-lg shadow-lg">
        9
      </div>
    ),
    ivanovo: () => (
      <div className="w-12 h-12 rounded-full bg-gradient-to-br from-sky-500 to-blue-600 flex items-center justify-center text-white font-bold text-lg shadow-lg">
        10
      </div>
    ),
    kabardino: () => (
      <div className="w-12 h-12 rounded-full bg-gradient-to-br from-lime-500 to-green-600 flex items-center justify-center text-white font-bold text-lg shadow-lg">
        11
      </div>
    ),
    kemerovo: () => (
      <div className="w-12 h-12 rounded-full bg-gradient-to-br from-orange-500 to-red-600 flex items-center justify-center text-white font-bold text-lg shadow-lg">
        12
      </div>
    ),
    lobachevsky: () => (
      <div className="w-12 h-12 rounded-full bg-gradient-to-br from-violet-500 to-purple-600 flex items-center justify-center text-white font-bold text-lg shadow-lg">
        13
      </div>
    ),
    "mephi-moscow": () => (
      <div className="w-12 h-12 rounded-full bg-gradient-to-br from-cyan-500 to-teal-600 flex items-center justify-center text-white font-bold text-lg shadow-lg">
        14
      </div>
    ),
    "mephi-obninsk": () => (
      <div className="w-12 h-12 rounded-full bg-gradient-to-br from-pink-500 to-rose-600 flex items-center justify-center text-white font-bold text-lg shadow-lg">
        15
      </div>
    ),
    "north-caucasian": () => (
      <div className="w-12 h-12 rounded-full bg-gradient-to-br from-emerald-500 to-green-600 flex items-center justify-center text-white font-bold text-lg shadow-lg">
        16
      </div>
    ),
    northwestern: () => (
      <div className="w-12 h-12 rounded-full bg-gradient-to-br from-indigo-600 to-blue-700 flex items-center justify-center text-white font-bold text-lg shadow-lg">
        17
      </div>
    ),
    omsk: () => (
      <div className="w-12 h-12 rounded-full bg-gradient-to-br from-amber-600 to-orange-700 flex items-center justify-center text-white font-bold text-lg shadow-lg">
        18
      </div>
    ),
    privolzhsky: () => (
      <div className="w-12 h-12 rounded-full bg-gradient-to-br from-teal-600 to-cyan-700 flex items-center justify-center text-white font-bold text-lg shadow-lg">
        19
      </div>
    ),
    rostov: () => (
      <div className="w-12 h-12 rounded-full bg-gradient-to-br from-red-600 to-pink-700 flex items-center justify-center text-white font-bold text-lg shadow-lg">
        20
      </div>
    ),
    "petersburg-vet": () => (
      <div className="w-12 h-12 rounded-full bg-gradient-to-br from-blue-600 to-indigo-700 flex items-center justify-center text-white font-bold text-lg shadow-lg">
        21
      </div>
    ),
    sevastopol: () => (
      <div className="w-12 h-12 rounded-full bg-gradient-to-br from-purple-600 to-violet-700 flex items-center justify-center text-white font-bold text-lg shadow-lg">
        22
      </div>
    ),
    siberian: () => (
      <div className="w-12 h-12 rounded-full bg-gradient-to-br from-green-600 to-emerald-700 flex items-center justify-center text-white font-bold text-lg shadow-lg">
        23
      </div>
    ),
    "synergy-moscow": () => (
      <div className="w-12 h-12 rounded-full bg-gradient-to-br from-yellow-600 to-amber-700 flex items-center justify-center text-white font-bold text-lg shadow-lg">
        24
      </div>
    ),
    tambov: () => (
      <div className="w-12 h-12 rounded-full bg-gradient-to-br from-rose-600 to-red-700 flex items-center justify-center text-white font-bold text-lg shadow-lg">
        25
      </div>
    ),
    tver: () => (
      <div className="w-12 h-12 rounded-full bg-gradient-to-br from-sky-600 to-blue-700 flex items-center justify-center text-white font-bold text-lg shadow-lg">
        26
      </div>
    ),
    ulyanovsk: () => (
      <div className="w-12 h-12 rounded-full bg-gradient-to-br from-lime-600 to-green-700 flex items-center justify-center text-white font-bold text-lg shadow-lg">
        27
      </div>
    ),
    volgograd: () => (
      <div className="w-12 h-12 rounded-full bg-gradient-to-br from-orange-600 to-red-700 flex items-center justify-center text-white font-bold text-lg shadow-lg">
        28
      </div>
    ),
    voronezh: () => (
      <div className="w-12 h-12 rounded-full bg-gradient-to-br from-violet-600 to-purple-700 flex items-center justify-center text-white font-bold text-lg shadow-lg">
        29
      </div>
    )
  };

  // Use universities data from the universities.js file (includes whyChoose data)
  const universities = useMemo(() => universitiesData, []);

  // Filter universities based on search term
  const filteredUniversities = useMemo(() => {
    if (!searchTerm) return universities;
    
    return universities.filter(university =>
      university.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      university.location.toLowerCase().includes(searchTerm.toLowerCase()) ||
      university.features.some(feature => 
        feature.toLowerCase().includes(searchTerm.toLowerCase())
      )
    );
  }, [searchTerm]);

  // Sort universities based on selected sort option
  const sortedUniversities = useMemo(() => {
    let universities = [...filteredUniversities];
    
    switch (sortOption) {
      case 'alphabetical':
        return universities.sort((a, b) => a.name.localeCompare(b.name));
      
      case 'lowToHigh':
        return universities.sort((a, b) => {
          const aCalculation = calculateTotalFirstYear(a);
          const bCalculation = calculateTotalFirstYear(b);
          return aCalculation.breakdown.totalINR - bCalculation.breakdown.totalINR;
        });
      
      case 'highToLow':
        return universities.sort((a, b) => {
          const aCalculation = calculateTotalFirstYear(a);
          const bCalculation = calculateTotalFirstYear(b);
          return bCalculation.breakdown.totalINR - aCalculation.breakdown.totalINR;
        });
      
      case 'whoNmcEnglish':
        // Filter universities that have WHO approved, NMC recognized, and English medium
        const qualifiedUniversities = universities.filter(university => {
          const features = university.features.map(f => f.toLowerCase());
          return features.includes('who approved') && 
                 features.includes('nmc recognized') && 
                 features.includes('english medium');
        });
        return qualifiedUniversities.sort((a, b) => a.name.localeCompare(b.name));
      
      default:
        return universities.sort((a, b) => a.name.localeCompare(b.name)); // Default to alphabetical
    }
  }, [filteredUniversities, sortOption, liveExchangeRates]);

  const handleUniversityClick = (university) => {
    setSelectedUniversity(university);
    setCurrentImageIndex(0); // Reset gallery to first image
    setImageLoadingStates({}); // Reset loading states
    setShowWhyChoose(false); // Close "Why choose this?" popup when opening new university
  };

  const closeModal = () => {
    setSelectedUniversity(null);
  };
  return (
    <div className="min-h-screen relative overflow-hidden custom-scrollbar select-none" 
         style={{
           userSelect: 'none',
           webkitUserSelect: 'none',
           mozUserSelect: 'none',
           msUserSelect: 'none',
           webkitTouchCallout: 'none',
           webkitUserDrag: 'none',
           webkitTapHighlightColor: 'transparent'
         }}
         onContextMenu={(e) => e.preventDefault()}
         onSelectStart={(e) => e.preventDefault()}
         onDragStart={(e) => e.preventDefault()}>
      <style jsx global>{`
        /* Global custom scrollbar styles */
        .custom-scrollbar {
          scrollbar-width: thin;
          scrollbar-color: ${darkMode ? '#3B82F6 rgba(55, 65, 81, 0.3)' : '#1D4ED8 rgba(229, 231, 235, 0.3)'};
        }

        /* Content Protection Styles */
        * {
          -webkit-user-select: none !important;
          -moz-user-select: none !important;
          -ms-user-select: none !important;
          user-select: none !important;
          -webkit-user-drag: none !important;
          -webkit-touch-callout: none !important;
          -webkit-tap-highlight-color: transparent !important;
        }

        /* Disable text selection highlighting */
        ::selection {
          background: transparent !important;
        }
        ::-moz-selection {
          background: transparent !important;
        }

        /* Disable image dragging */
        img {
          -webkit-user-drag: none !important;
          -moz-user-drag: none !important;
          user-drag: none !important;
          pointer-events: none !important;
        }

        /* Hide text cursor */
        input, textarea {
          caret-color: transparent !important;
        }
        
        /* Disable print styles */
        @media print {
          body { display: none !important; }
        }
        
        .custom-scrollbar::-webkit-scrollbar {
          width: 12px;
          height: 12px;
        }
        
        .custom-scrollbar::-webkit-scrollbar-track {
          background: ${darkMode ? 'rgba(55, 65, 81, 0.3)' : 'rgba(229, 231, 235, 0.3)'};
          border-radius: 12px;
          margin: 8px;
          border: 1px solid ${darkMode ? 'rgba(75, 85, 99, 0.2)' : 'rgba(209, 213, 219, 0.2)'};
        }
        
        .custom-scrollbar::-webkit-scrollbar-thumb {
          background: linear-gradient(135deg, #3B82F6, #8B5CF6, #06B6D4);
          border-radius: 12px;
          border: 2px solid ${darkMode ? 'rgba(31, 41, 55, 0.8)' : 'rgba(255, 255, 255, 0.8)'};
          box-shadow: 
            0 4px 8px rgba(59, 130, 246, 0.3),
            0 2px 4px rgba(139, 92, 246, 0.2),
            inset 0 1px 2px rgba(255, 255, 255, 0.3);
          transition: all 0.3s ease;
        }
        
        .custom-scrollbar::-webkit-scrollbar-thumb:hover {
          background: linear-gradient(135deg, #2563EB, #7C3AED, #0891B2);
          box-shadow: 
            0 6px 12px rgba(59, 130, 246, 0.4),
            0 3px 6px rgba(139, 92, 246, 0.3),
            inset 0 1px 2px rgba(255, 255, 255, 0.4),
            0 0 12px rgba(59, 130, 246, 0.6);
          transform: scale(1.05);
        }
        
        .custom-scrollbar::-webkit-scrollbar-thumb:active {
          background: linear-gradient(135deg, #1D4ED8, #6D28D9, #0369A1);
          box-shadow: 
            0 2px 4px rgba(59, 130, 246, 0.5),
            inset 0 2px 4px rgba(0, 0, 0, 0.2);
          transform: scale(0.95);
        }
        
        .custom-scrollbar::-webkit-scrollbar-corner {
          background: ${darkMode ? 'rgba(55, 65, 81, 0.3)' : 'rgba(229, 231, 235, 0.3)'};
          border-radius: 12px;
        }
        
        /* Enhanced mobile styles */
        @media (max-width: 768px) {
          .custom-scrollbar::-webkit-scrollbar {
            width: 10px;
            height: 10px;
          }
          
          .custom-scrollbar::-webkit-scrollbar-track {
            margin: 6px;
            border-radius: 10px;
          }
          
          .custom-scrollbar::-webkit-scrollbar-thumb {
            border-radius: 10px;
            border: 1px solid ${darkMode ? 'rgba(31, 41, 55, 0.8)' : 'rgba(255, 255, 255, 0.8)'};
            box-shadow: 
              0 3px 6px rgba(59, 130, 246, 0.4),
              0 1px 3px rgba(139, 92, 246, 0.3),
              inset 0 1px 1px rgba(255, 255, 255, 0.4);
          }
          
          .custom-scrollbar::-webkit-scrollbar-thumb:hover {
            transform: scale(1.1);
            box-shadow: 
              0 4px 8px rgba(59, 130, 246, 0.5),
              0 2px 4px rgba(139, 92, 246, 0.4),
              inset 0 1px 2px rgba(255, 255, 255, 0.5),
              0 0 8px rgba(59, 130, 246, 0.7);
          }
        }
        
        /* Apply to body for page-level scroll */
        body {
          scrollbar-width: thin;
          scrollbar-color: ${darkMode ? '#3B82F6 rgba(55, 65, 81, 0.3)' : '#1D4ED8 rgba(229, 231, 235, 0.3)'};
        }
        
        body::-webkit-scrollbar {
          width: 12px;
        }
        
        body::-webkit-scrollbar-track {
          background: ${darkMode ? 'rgba(55, 65, 81, 0.3)' : 'rgba(229, 231, 235, 0.3)'};
          border-radius: 12px;
          margin: 8px;
        }
        
        body::-webkit-scrollbar-thumb {
          background: linear-gradient(135deg, #3B82F6, #8B5CF6, #06B6D4);
          border-radius: 12px;
          border: 2px solid ${darkMode ? 'rgba(31, 41, 55, 0.8)' : 'rgba(255, 255, 255, 0.8)'};
          box-shadow: 
            0 4px 8px rgba(59, 130, 246, 0.3),
            0 2px 4px rgba(139, 92, 246, 0.2),
            inset 0 1px 2px rgba(255, 255, 255, 0.3);
          transition: all 0.3s ease;
        }
        
        body::-webkit-scrollbar-thumb:hover {
          background: linear-gradient(135deg, #2563EB, #7C3AED, #0891B2);
          box-shadow: 
            0 6px 12px rgba(59, 130, 246, 0.4),
            0 3px 6px rgba(139, 92, 246, 0.3),
            inset 0 1px 2px rgba(255, 255, 255, 0.4),
            0 0 12px rgba(59, 130, 246, 0.6);
          transform: scale(1.05);
        }
        
        /* Apply to all scrollable containers */
        * {
          scrollbar-width: thin;
          scrollbar-color: ${darkMode ? '#3B82F6 rgba(55, 65, 81, 0.3)' : '#1D4ED8 rgba(229, 231, 235, 0.3)'};
        }
        
        *::-webkit-scrollbar {
          width: 12px;
          height: 12px;
        }
        
        *::-webkit-scrollbar-track {
          background: ${darkMode ? 'rgba(55, 65, 81, 0.3)' : 'rgba(229, 231, 235, 0.3)'};
          border-radius: 12px;
          margin: 8px;
          border: 1px solid ${darkMode ? 'rgba(75, 85, 99, 0.2)' : 'rgba(209, 213, 219, 0.2)'};
        }
        
        *::-webkit-scrollbar-thumb {
          background: linear-gradient(135deg, #3B82F6, #8B5CF6, #06B6D4);
          border-radius: 12px;
          border: 2px solid ${darkMode ? 'rgba(31, 41, 55, 0.8)' : 'rgba(255, 255, 255, 0.8)'};
          box-shadow: 
            0 4px 8px rgba(59, 130, 246, 0.3),
            0 2px 4px rgba(139, 92, 246, 0.2),
            inset 0 1px 2px rgba(255, 255, 255, 0.3);
          transition: all 0.3s ease;
        }
        
        *::-webkit-scrollbar-thumb:hover {
          background: linear-gradient(135deg, #2563EB, #7C3AED, #0891B2);
          box-shadow: 
            0 6px 12px rgba(59, 130, 246, 0.4),
            0 3px 6px rgba(139, 92, 246, 0.3),
            inset 0 1px 2px rgba(255, 255, 255, 0.4),
            0 0 12px rgba(59, 130, 246, 0.6);
          transform: scale(1.05);
        }
        
        *::-webkit-scrollbar-thumb:active {
          background: linear-gradient(135deg, #1D4ED8, #6D28D9, #0369A1);
          box-shadow: 
            0 2px 4px rgba(59, 130, 246, 0.5),
            inset 0 2px 4px rgba(0, 0, 0, 0.2);
          transform: scale(0.95);
        }
        
        *::-webkit-scrollbar-corner {
          background: ${darkMode ? 'rgba(55, 65, 81, 0.3)' : 'rgba(229, 231, 235, 0.3)'};
          border-radius: 12px;
        }
        
        @media (max-width: 768px) {
          body::-webkit-scrollbar, *::-webkit-scrollbar {
            width: 10px;
            height: 10px;
          }
          
          body::-webkit-scrollbar-track, *::-webkit-scrollbar-track {
            margin: 6px;
            border-radius: 10px;
          }
          
          body::-webkit-scrollbar-thumb, *::-webkit-scrollbar-thumb {
            border-radius: 10px;
            border: 1px solid ${darkMode ? 'rgba(31, 41, 55, 0.8)' : 'rgba(255, 255, 255, 0.8)'};
            box-shadow: 
              0 3px 6px rgba(59, 130, 246, 0.4),
              0 1px 3px rgba(139, 92, 246, 0.3),
              inset 0 1px 1px rgba(255, 255, 255, 0.4);
          }
          
          body::-webkit-scrollbar-thumb:hover, *::-webkit-scrollbar-thumb:hover {
            transform: scale(1.1);
            box-shadow: 
              0 4px 8px rgba(59, 130, 246, 0.5),
              0 2px 4px rgba(139, 92, 246, 0.4),
              inset 0 1px 2px rgba(255, 255, 255, 0.5),
              0 0 8px rgba(59, 130, 246, 0.7);
          }
        }
      `}</style>
      {/* Black glassmorphism background */}
      <div className="fixed inset-0 z-0 pointer-events-none overflow-hidden">
        <div className="absolute inset-0 bg-black"></div>
        <div className="absolute top-[-10%] left-[-10%] w-[50vw] h-[50vw] bg-blue-900/30 rounded-full blur-[120px]"></div>
        <div className="absolute bottom-[-10%] right-[-10%] w-[50vw] h-[50vw] bg-indigo-900/20 rounded-full blur-[120px]"></div>
        <div className="absolute top-[40%] left-[50%] transform -translate-x-1/2 w-[60vw] h-[40vw] bg-purple-900/10 rounded-full blur-[100px]"></div>
      </div>
      
      <div className="relative z-10 min-h-screen">
      {/* Header */}
      <div className="backdrop-blur-md bg-black/50 border-b border-white/10 shadow-lg">
        <div className="container mx-auto px-4 sm:px-6 py-6 sm:py-8">
          <div className="text-center mb-6 sm:mb-8">
            <div className="flex justify-center mb-3 sm:mb-4">
              <svg className="w-10 h-10 sm:w-12 sm:h-12 text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 14l9-5-9-5-9 5 9 5zm0 0l6.16-3.422a12.083 12.083 0 01.665 6.479A11.952 11.952 0 0012 20.055a11.952 11.952 0 00-6.824-2.998 12.078 12.078 0 01.665-6.479L12 14z" />
              </svg>
            </div>
            <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold mb-2 text-white">University Fee Structures</h1>
            <p className="text-base sm:text-lg px-2 text-gray-300">
              Compare fees from 29 top Russian medical universities
            </p>
          </div>

          {/* Search Bar */}
          <div className="max-w-2xl mx-auto relative">
            <div className="relative">
              <svg className="w-5 h-5 absolute left-3 sm:left-4 top-1/2 transform -translate-y-1/2 text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
              <input
                type="text"
                placeholder="Search universities..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 sm:pl-12 pr-3 sm:pr-4 py-3 sm:py-4 rounded-3xl lg:rounded-full border-2 border-white/20 backdrop-blur-md bg-black/30 text-white placeholder-white/70 focus:outline-none focus:ring-2 focus:ring-blue-400/50 focus:border-blue-400/50 text-base sm:text-lg"
              />
            </div>            <div className="mt-2 text-sm text-center sm:text-left text-gray-400">
              Found {filteredUniversities.length} universities
            </div>
          </div>          {/* Sort Options */}
          <div className="max-w-4xl mx-auto mt-6 sm:mt-8">
            {/* Main Sort Button */}
            <div className="flex justify-center mb-4">
              <button
                onClick={() => setShowSortOptions(!showSortOptions)}
                className={`px-4 sm:px-6 py-3 rounded-3xl font-medium text-sm sm:text-base transition-all duration-300 flex items-center gap-2 ${
                  showSortOptions
                    ? 'bg-blue-600/80 backdrop-blur-md text-white shadow-lg border border-blue-400/30'
                    : 'backdrop-blur-md bg-white/10 text-white border border-white/20 hover:bg-white/20'
                }`}
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16V4m0 0L3 8m4-4l4 4m6 0v12m0 0l4-4m-4 4l-4-4" />
                </svg>
                Sort
                <svg 
                  className={`w-4 h-4 transition-transform duration-300 ${showSortOptions ? 'rotate-180' : ''}`} 
                  fill="none" 
                  stroke="currentColor" 
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M19 9l-7 7-7-7" />
                </svg>
              </button>
            </div>

            {/* Sort Options - Hidden by default, shown when Sort button is clicked */}
            <div className={`transition-all duration-300 ease-in-out ${showSortOptions ? 'opacity-100 max-h-96' : 'opacity-0 max-h-0 overflow-hidden'}`}>
              <div className="flex flex-wrap justify-center gap-2 sm:gap-3">
                <button
                  onClick={() => setSortOption('alphabetical')}
                  className={`px-3 sm:px-4 py-2 rounded-3xl font-medium text-sm sm:text-base transition-colors flex items-center gap-2 ${
                    sortOption === 'alphabetical'
                      ? 'bg-indigo-600/80 backdrop-blur-md text-white shadow-lg border border-indigo-400/30'
                      : 'backdrop-blur-md bg-white/10 text-white border border-white/20 hover:bg-white/20'
                  }`}
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 10h12M4 14h8M4 18h4" />
                  </svg>
                  A-Z
                </button>
                <button
                  onClick={() => setSortOption('lowToHigh')}
                  className={`px-3 sm:px-4 py-2 rounded-3xl font-medium text-sm sm:text-base transition-colors flex items-center gap-2 ${
                    sortOption === 'lowToHigh'
                      ? 'bg-green-600/80 backdrop-blur-md text-white shadow-lg border border-green-400/30'
                      : 'backdrop-blur-md bg-white/10 text-white border border-white/20 hover:bg-white/20'
                  }`}
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16V4m0 0L3 8m4-4l4 4m6 0v12m0 0l4-4m-4 4l-4-4" />
                  </svg>
                  Price: Low to High
                </button>
                <button
                  onClick={() => setSortOption('highToLow')}
                  className={`px-3 sm:px-4 py-2 rounded-3xl font-medium text-sm sm:text-base transition-colors flex items-center gap-2 ${
                    sortOption === 'highToLow'
                      ? 'bg-red-600/80 backdrop-blur-md text-white shadow-lg border border-red-400/30'
                      : 'backdrop-blur-md bg-white/10 text-white border border-white/20 hover:bg-white/20'
                  }`}
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 4v12m0 0L3 12m4 4l4-4m6 8V8m0 0l4 4m-4-4l-4 4" />
                  </svg>
                  Price: High to Low
                </button>
                <button
                  onClick={() => setSortOption('whoNmcEnglish')}
                  className={`px-3 sm:px-4 py-2 rounded-3xl font-medium text-sm sm:text-base transition-colors flex items-center gap-2 ${
                    sortOption === 'whoNmcEnglish'
                      ? 'bg-purple-600/80 backdrop-blur-md text-white shadow-lg border border-purple-400/30'
                      : 'backdrop-blur-md bg-white/10 text-white border border-white/20 hover:bg-white/20'
                  }`}
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  WHO + NMC + English
                </button>
              </div>
            </div>

            {/* Currently Sorting Status */}
            <div className="text-center mt-3 text-xs sm:text-sm text-gray-300">
              Currently sorting: <span className="font-medium text-white">
                {sortOption === 'alphabetical' && 'Alphabetical Order (A-Z)'}
                {sortOption === 'lowToHigh' && 'Price: Low to High'}
                {sortOption === 'highToLow' && 'Price: High to Low'}
                {sortOption === 'whoNmcEnglish' && 'WHO + NMC + English Medium Universities'}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Universities Grid */}
      <div className="container mx-auto px-4 sm:px-6 py-6 sm:py-8">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 sm:gap-6">          {sortedUniversities.map((university) => {
            const IconComponent = UniversityIcons[university.icon];
            const images = getUniversityImages(university.name);
            
            // Skip rendering if IconComponent is undefined
            if (!IconComponent) {
              console.warn(`Icon not found for university: ${university.name} with icon: ${university.icon}`);
              return null;
            }
            
            return (
              <div
                key={university.id}
                onClick={() => handleUniversityClick(university)}
                className="p-4 sm:p-6 rounded-xl backdrop-blur-md bg-white/10 border border-white/20 shadow-lg cursor-pointer transform hover:scale-105 transition-all duration-300 hover:bg-white/20 hover:shadow-xl"
              >
                {/* University Icon & Profile Image */}
                <div className="flex flex-col items-center mb-3 sm:mb-4">
                  {/* SVG Number Icon */}
                  <div className="p-2 sm:p-3 rounded-full bg-indigo-100 dark:bg-indigo-900/30 mb-3">
                    <IconComponent />
                  </div>
                    {/* Profile Image */}
                  {images && (
                    <div className="w-16 h-16 sm:w-20 sm:h-20 rounded-full overflow-hidden shadow-lg border-3 border-white dark:border-gray-700 hover:shadow-xl transition-shadow duration-300">
                      <img 
                        src={images.profile} 
                        alt={`${university.name} Profile`}
                        className="w-full h-full object-cover hover:scale-110 transition-transform duration-300"
                        onError={(e) => {
                          // Hide broken images and show fallback
                          e.target.style.display = 'none';
                          e.target.nextElementSibling.style.display = 'flex';
                        }}
                      />
                      {/* Fallback for broken images */}
                      <div 
                        className={`w-full h-full flex items-center justify-center ${darkMode ? 'bg-gray-600' : 'bg-gray-200'}`}
                        style={{display: 'none'}}
                      >
                        <IconComponent />
                      </div>
                    </div>
                  )}
                  
                  {/* Fallback when no images available */}
                  {!images && (
                    <div className={`w-16 h-16 sm:w-20 sm:h-20 rounded-full flex items-center justify-center ${darkMode ? 'bg-gray-600' : 'bg-gray-200'} shadow-lg border-3 border-white dark:border-gray-700`}>
                      <IconComponent />
                    </div>
                  )}
                </div>

                {/* University Name */}
                <h3 className="text-base sm:text-lg font-bold text-center mb-2 line-clamp-2 leading-tight text-white">
                  {university.name}
                </h3>

                {/* Location */}
                <p className="text-xs sm:text-sm text-center mb-3 flex items-center justify-center gap-1 text-gray-300">
                  <MatrixLocation size="12" className="matrix-emoji-red flex-shrink-0" />
                  <span className="truncate">{university.location}</span>
                </p>

                {/* Fee Information */}
                <div className="space-y-2 mb-3 sm:mb-4">
                  <div className="flex justify-between items-center text-xs sm:text-sm">
                    <span className="font-medium text-gray-200">Tuition:</span>
                    <span className="font-bold text-blue-300 text-right">
                      {formatFeeForCard(university.tuition)}
                    </span>
                  </div>
                  {/* Horizontal line above 1st Year Total */}
                  <div className="border-t border-gray-600/30 my-2"></div>
                  <div className="flex justify-between items-center">
                    <span className="text-xs sm:text-sm font-medium text-gray-200">1st Year Total:</span>
                    <span className="text-base sm:text-lg font-bold text-green-400">
                      {(() => {
                        const calculation = calculateTotalFirstYear(university);
                        return calculation.totalINR;
                      })()}
                    </span>
                  </div>
                  <div className="text-center">
                    <span className="text-xs sm:text-sm text-gray-400">
                      ({(() => {
                        const calculation = calculateTotalFirstYear(university);
                        return `$${Math.round(calculation.breakdown.totalRUB / 91).toLocaleString('en-IN')}`;
                      })()})
                    </span>
                  </div>
                </div>

                {/* Features */}
                <div className="space-y-1 mb-3">
                  {university.features.slice(0, 2).map((feature, index) => (
                    <div key={index} className="flex items-center">
                      <span className="text-green-400 mr-2 text-xs">✓</span>
                      <span className="text-xs sm:text-sm truncate text-gray-200">{feature}</span>
                    </div>
                  ))}
                </div>

                {/* Click to view more */}
                <div className="text-center">
                  <span className="text-xs text-blue-300 font-medium">
                    Tap for detailed breakdown
                  </span>
                </div>
              </div>
            );
          })}
        </div>

        {filteredUniversities.length === 0 && (
          <div className="text-center py-12">
            <MatrixChart size="64" className="matrix-emoji-gold mx-auto mb-4" />
            <h3 className="text-2xl font-bold mb-2 text-white">No universities found</h3>
            <p className="text-gray-300">
              Try adjusting your search terms
            </p>
          </div>
        )}
      </div>      {/* Detailed University Modal */}
      {selectedUniversity && (
        <div className={`fixed inset-0 ${darkMode ? 'bg-black/70' : 'bg-gray-900/50'} backdrop-blur-sm flex items-start justify-center pt-4 pb-4 px-2 sm:px-4 overflow-y-auto z-50`}>
          <div className={`w-full max-w-4xl min-h-fit my-2 rounded-2xl shadow-2xl backdrop-blur-xl ${darkMode ? 'bg-gray-900/90 border-gray-700/50' : 'bg-white/95 border-gray-200/50'} border relative custom-scrollbar`}>
            <style jsx>{`
              .custom-scrollbar {
                /* Beautiful scrollbar for all screen sizes */
                scrollbar-width: thin;
                scrollbar-color: ${darkMode ? '#3B82F6 rgba(55, 65, 81, 0.3)' : '#1D4ED8 rgba(229, 231, 235, 0.3)'};
              }
              
              .custom-scrollbar::-webkit-scrollbar {
                width: 12px;
                height: 12px;
              }
              
              .custom-scrollbar::-webkit-scrollbar-track {
                background: ${darkMode ? 'rgba(55, 65, 81, 0.3)' : 'rgba(229, 231, 235, 0.3)'};
                border-radius: 12px;
                margin: 8px;
                border: 1px solid ${darkMode ? 'rgba(75, 85, 99, 0.2)' : 'rgba(209, 213, 219, 0.2)'};
              }
              
              .custom-scrollbar::-webkit-scrollbar-thumb {
                background: linear-gradient(135deg, #3B82F6, #8B5CF6, #06B6D4);
                border-radius: 12px;
                border: 2px solid ${darkMode ? 'rgba(31, 41, 55, 0.8)' : 'rgba(255, 255, 255, 0.8)'};
                box-shadow: 
                  0 4px 8px rgba(59, 130, 246, 0.3),
                  0 2px 4px rgba(139, 92, 246, 0.2),
                  inset 0 1px 2px rgba(255, 255, 255, 0.3);
                transition: all 0.3s ease;
              }
              
              .custom-scrollbar::-webkit-scrollbar-thumb:hover {
                background: linear-gradient(135deg, #2563EB, #7C3AED, #0891B2);
                box-shadow: 
                  0 6px 12px rgba(59, 130, 246, 0.4),
                  0 3px 6px rgba(139, 92, 246, 0.3),
                  inset 0 1px 2px rgba(255, 255, 255, 0.4),
                  0 0 12px rgba(59, 130, 246, 0.6);
                transform: scale(1.05);
              }
              
              .custom-scrollbar::-webkit-scrollbar-thumb:active {
                background: linear-gradient(135deg, #1D4ED8, #6D28D9, #0369A1);
                box-shadow: 
                  0 2px 4px rgba(59, 130, 246, 0.5),
                  inset 0 2px 4px rgba(0, 0, 0, 0.2);
                transform: scale(0.95);
              }
              
              .custom-scrollbar::-webkit-scrollbar-corner {
                background: ${darkMode ? 'rgba(55, 65, 81, 0.3)' : 'rgba(229, 231, 235, 0.3)'};
                border-radius: 12px;
              }
              
              /* Enhanced mobile styles */
              @media (max-width: 768px) {
                .custom-scrollbar::-webkit-scrollbar {
                  width: 10px;
                  height: 10px;
                }
                
                .custom-scrollbar::-webkit-scrollbar-track {
                  margin: 6px;
                  border-radius: 10px;
                }
                
                .custom-scrollbar::-webkit-scrollbar-thumb {
                  border-radius: 10px;
                  border: 1px solid ${darkMode ? 'rgba(31, 41, 55, 0.8)' : 'rgba(255, 255, 255, 0.8)'};
                  box-shadow: 
                    0 3px 6px rgba(59, 130, 246, 0.4),
                    0 1px 3px rgba(139, 92, 246, 0.3),
                    inset 0 1px 1px rgba(255, 255, 255, 0.4);
                }
                
                .custom-scrollbar::-webkit-scrollbar-thumb:hover {
                  transform: scale(1.1);
                  box-shadow: 
                    0 4px 8px rgba(59, 130, 246, 0.5),
                    0 2px 4px rgba(139, 92, 246, 0.4),
                    inset 0 1px 2px rgba(255, 255, 255, 0.5),
                    0 0 8px rgba(59, 130, 246, 0.7);
                }
              }
            `}</style>
            
            {/* Inner scrollable content */}
            <div className="overflow-y-auto max-h-[calc(100vh-4rem)] custom-scrollbar">
            
            {/* Background Blur Elements */}
            <div className="absolute inset-0 overflow-hidden pointer-events-none rounded-2xl">
              <div className={`absolute top-10 left-10 w-32 h-32 ${darkMode ? 'bg-gradient-to-r from-blue-400/20 to-purple-400/20' : 'bg-gradient-to-r from-blue-400/10 to-purple-400/10'} rounded-full blur-2xl`}></div>
              <div className={`absolute top-20 right-10 w-24 h-24 ${darkMode ? 'bg-gradient-to-r from-pink-400/20 to-orange-400/20' : 'bg-gradient-to-r from-pink-400/10 to-orange-400/10'} rounded-full blur-2xl`}></div>
              <div className={`absolute bottom-10 left-1/3 w-20 h-20 ${darkMode ? 'bg-gradient-to-r from-green-400/20 to-blue-400/20' : 'bg-gradient-to-r from-green-400/10 to-blue-400/10'} rounded-full blur-2xl`}></div>
            </div>
            
            <div className="pt-8 pb-6 px-4 sm:pt-6 sm:pb-6 sm:px-6 lg:pt-8 lg:pb-6 lg:px-8 relative z-10">
              {/* Enhanced Header */}
              <div className="flex justify-between items-start mb-4 sm:mb-6 lg:mb-8">
                <div className="flex items-center flex-1 mr-3 sm:mr-4">
                  <div className={`p-2 sm:p-3 lg:p-4 rounded-2xl ${darkMode ? 'bg-indigo-500/20 border-indigo-400/30' : 'bg-indigo-500/10 border-indigo-400/20'} backdrop-blur-sm border mr-3 sm:mr-4 lg:mr-6 flex-shrink-0 shadow-lg`}>
                    {UniversityIcons[selectedUniversity.icon] && 
                      React.createElement(UniversityIcons[selectedUniversity.icon])}
                  </div>
                  <div className="min-w-0 flex-1">
                    <h2 className={`text-lg sm:text-xl lg:text-2xl xl:text-3xl font-bold leading-tight ${darkMode ? 'text-white' : 'text-gray-900'} mb-1 sm:mb-2 bg-gradient-to-r from-blue-400 to-purple-400 bg-clip-text text-transparent`}>
                      {selectedUniversity.name}
                    </h2>
                    <div className="flex items-center gap-1.5 sm:gap-2 mb-2 sm:mb-3">
                      <div className="p-1 sm:p-1.5 rounded-lg bg-red-500/20 backdrop-blur-sm">
                        <MatrixLocation size="14" className="matrix-emoji-red flex-shrink-0 sm:hidden" />
                        <MatrixLocation size="16" className="matrix-emoji-red flex-shrink-0 hidden sm:block" />
                      </div>
                      <span className={`${darkMode ? 'text-gray-200' : 'text-gray-700'} text-xs sm:text-sm lg:text-base font-medium`}>{selectedUniversity.location}</span>
                    </div>
                    
                    {/* Quick Stats Pills */}
                    <div className="flex flex-wrap gap-1.5 sm:gap-2">
                      <span className={`px-2 sm:px-3 py-1 sm:py-1.5 rounded-full ${darkMode ? 'bg-green-500/20 text-green-300 border-green-400/30' : 'bg-green-500/10 text-green-600 border-green-400/20'} text-xs font-medium border backdrop-blur-sm`}>
                        ✓ WHO Approved
                      </span>
                      <span className={`px-2 sm:px-3 py-1 sm:py-1.5 rounded-full ${darkMode ? 'bg-blue-500/20 text-blue-300 border-blue-400/30' : 'bg-blue-500/10 text-blue-600 border-blue-400/20'} text-xs font-medium border backdrop-blur-sm`}>
                        ✓ NMC Recognized
                      </span>
                      <span className={`px-2 sm:px-3 py-1 sm:py-1.5 rounded-full ${darkMode ? 'bg-purple-500/20 text-purple-300 border-purple-400/30' : 'bg-purple-500/10 text-purple-600 border-purple-400/20'} text-xs font-medium border backdrop-blur-sm`}>
                        English Medium
                      </span>
                    </div>
                    
                    {/* Why Choose This Button */}
                    <div className="mt-3 sm:mt-4">
                      <button
                        onClick={() => setShowWhyChoose(!showWhyChoose)}
                        className={`px-3 sm:px-4 py-2 sm:py-2.5 rounded-xl ${darkMode ? 'bg-gradient-to-r from-indigo-500/20 to-purple-500/20 hover:from-indigo-500/30 hover:to-purple-500/30 text-indigo-300 border-indigo-400/30' : 'bg-gradient-to-r from-indigo-500/10 to-purple-500/10 hover:from-indigo-500/20 hover:to-purple-500/20 text-indigo-600 border-indigo-400/20'} text-xs sm:text-sm font-semibold border backdrop-blur-sm transition-all duration-300 flex items-center gap-2 hover:scale-105 shadow-lg hover:shadow-xl`}
                      >
                        <svg className="w-4 h-4 sm:w-5 sm:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                        </svg>
                        Why choose this?
                        <svg className={`w-3 h-3 sm:w-4 sm:h-4 transition-transform duration-300 ${showWhyChoose ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                        </svg>
                      </button>
                    </div>
                  </div>
                </div>
                <button
                  onClick={closeModal}
                  className={`p-3 rounded-full ${darkMode ? 'hover:bg-white/20 border-white/20 text-white' : 'hover:bg-gray-100 border-gray-300 text-gray-700'} transition-all duration-300 flex-shrink-0 backdrop-blur-sm border shadow-lg hover:shadow-xl hover:scale-105`}
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              {/* Why Choose This Expandable Section */}
              {showWhyChoose && (() => {
                const whyChooseData = getUniversityWhyChooseData(selectedUniversity);
                
                return (
                  <div className={`rounded-2xl backdrop-blur-xl ${darkMode ? 'bg-gradient-to-br from-indigo-500/10 to-purple-500/10 border-indigo-400/30' : 'bg-gradient-to-br from-indigo-500/5 to-purple-500/5 border-indigo-400/20'} border shadow-2xl mb-6 sm:mb-8 overflow-hidden animate-in slide-in-from-top-4 duration-300`}>
                    <div className="p-4 sm:p-6 lg:p-8">
                      <h3 className="text-lg sm:text-xl lg:text-2xl font-bold mb-4 sm:mb-6 flex items-center gap-3 bg-gradient-to-r from-indigo-400 to-purple-400 bg-clip-text text-transparent">
                        <div className={`p-2 sm:p-3 rounded-xl ${darkMode ? 'bg-indigo-500/20 border-indigo-400/30' : 'bg-indigo-500/10 border-indigo-400/20'} backdrop-blur-sm border`}>
                          <svg className="w-5 h-5 sm:w-6 sm:h-6 text-indigo-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                          </svg>
                        </div>
                        Why Choose {selectedUniversity.name}?
                      </h3>
                      
                      <div className="space-y-4 sm:space-y-6">
                        {/* University Description */}
                        <div className={`p-4 sm:p-5 rounded-xl ${darkMode ? 'bg-gray-700/20 border-gray-600/20' : 'bg-gray-50/50 border-gray-200/30'} backdrop-blur-sm border`}>
                          <h4 className={`text-base sm:text-lg font-semibold mb-3 ${darkMode ? 'text-indigo-300' : 'text-indigo-600'}`}>
                            About the University
                          </h4>
                          <p className={`${darkMode ? 'text-gray-200' : 'text-gray-700'} text-sm sm:text-base leading-relaxed`}>
                            {selectedUniversity.description}
                          </p>
                        </div>
                        
                        {/* Medical Specialities */}
                        <div className={`p-3 sm:p-4 rounded-xl ${darkMode ? 'bg-blue-500/10 border-blue-400/20' : 'bg-blue-50/50 border-blue-200/30'} backdrop-blur-sm border`}>
                          <h4 className={`text-base sm:text-lg font-semibold mb-2 ${darkMode ? 'text-blue-300' : 'text-blue-600'} flex items-center gap-2`}>
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19.428 15.428a2 2 0 00-1.022-.547l-2.387-.477a6 6 0 00-3.86.517l-.318.158a6 6 0 01-3.86.517L6.05 15.21a2 2 0 00-1.806.547M8 4h8l-1 1v5.172a2 2 0 00.586 1.414l5 5c1.26 1.26.367 3.414-1.415 3.414H4.828c-1.782 0-2.674-2.154-1.414-3.414l5-5A2 2 0 009 10.172V5L8 4z" />
                            </svg>
                            Medical Specialities & Programs
                          </h4>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-1 sm:gap-2">
                            {(whyChooseData.specialities || []).map((speciality, index) => (
                              <div key={index} className="flex items-start gap-2 p-1.5 rounded-lg hover:bg-blue-500/5 transition-colors">
                                <div className={`w-1.5 h-1.5 rounded-full ${darkMode ? 'bg-blue-400' : 'bg-blue-500'} flex-shrink-0 mt-2`}></div>
                                <span className={`${darkMode ? 'text-gray-200' : 'text-gray-700'} text-xs sm:text-sm leading-snug`}>{speciality}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                        
                        {/* Clinical Features & Facilities */}
                        <div className={`p-3 sm:p-4 rounded-xl ${darkMode ? 'bg-purple-500/10 border-purple-400/20' : 'bg-purple-50/50 border-purple-200/30'} backdrop-blur-sm border`}>
                          <h4 className={`text-base sm:text-lg font-semibold mb-2 ${darkMode ? 'text-purple-300' : 'text-purple-600'} flex items-center gap-2`}>
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                            </svg>
                            Clinical Features & Hospital Facilities
                          </h4>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-1 sm:gap-2">
                            {(whyChooseData.clinicalFeatures || []).map((feature, index) => (
                              <div key={index} className="flex items-start gap-2 p-1.5 rounded-lg hover:bg-purple-500/5 transition-colors">
                                <div className={`w-1.5 h-1.5 rounded-full ${darkMode ? 'bg-purple-400' : 'bg-purple-500'} flex-shrink-0 mt-2`}></div>
                                <span className={`${darkMode ? 'text-gray-200' : 'text-gray-700'} text-xs sm:text-sm leading-snug`}>{feature}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                        
                        {/* Hostel & Accommodation */}
                        <div className={`p-3 sm:p-4 rounded-xl ${darkMode ? 'bg-emerald-500/10 border-emerald-400/20' : 'bg-emerald-50/50 border-emerald-200/30'} backdrop-blur-sm border`}>
                          <h4 className={`text-base sm:text-lg font-semibold mb-2 ${darkMode ? 'text-emerald-300' : 'text-emerald-600'} flex items-center gap-2`}>
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
                            </svg>
                            Hostel & Accommodation Facilities
                          </h4>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-1 sm:gap-2">
                            {(whyChooseData.hostelFacilities || []).map((facility, index) => (
                              <div key={index} className="flex items-start gap-2 p-1.5 rounded-lg hover:bg-emerald-500/5 transition-colors">
                                <div className={`w-1.5 h-1.5 rounded-full ${darkMode ? 'bg-emerald-400' : 'bg-emerald-500'} flex-shrink-0 mt-2`}></div>
                                <span className={`${darkMode ? 'text-gray-200' : 'text-gray-700'} text-xs sm:text-sm leading-snug`}>{facility}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                        
                        {/* Practical Exposure & Training */}
                        <div className={`p-3 sm:p-4 rounded-xl ${darkMode ? 'bg-orange-500/10 border-orange-400/20' : 'bg-orange-50/50 border-orange-200/30'} backdrop-blur-sm border`}>
                          <h4 className={`text-base sm:text-lg font-semibold mb-2 ${darkMode ? 'text-orange-300' : 'text-orange-600'} flex items-center gap-2`}>
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                            </svg>
                            Practical Exposure & Clinical Training
                          </h4>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-1 sm:gap-2">
                            {(whyChooseData.practicalExposure || []).map((exposure, index) => (
                              <div key={index} className="flex items-start gap-2 p-1.5 rounded-lg hover:bg-orange-500/5 transition-colors">
                                <div className={`w-1.5 h-1.5 rounded-full ${darkMode ? 'bg-orange-400' : 'bg-orange-500'} flex-shrink-0 mt-2`}></div>
                                <span className={`${darkMode ? 'text-gray-200' : 'text-gray-700'} text-xs sm:text-sm leading-snug`}>{exposure}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                        
                        {/* Location Benefits */}
                        <div className={`p-3 sm:p-4 rounded-xl ${darkMode ? 'bg-red-500/10 border-red-400/20' : 'bg-red-50/50 border-red-200/30'} backdrop-blur-sm border`}>
                          <h4 className={`text-base sm:text-lg font-semibold mb-2 ${darkMode ? 'text-red-300' : 'text-red-600'} flex items-center gap-2`}>
                            <MatrixLocation size="20" className="matrix-emoji-red" />
                            Location & Environment Benefits
                          </h4>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-1 sm:gap-2">
                            {(whyChooseData.locationBenefits || []).map((benefit, index) => (
                              <div key={index} className="flex items-start gap-2 p-1.5 rounded-lg hover:bg-red-500/5 transition-colors">
                                <div className={`w-1.5 h-1.5 rounded-full ${darkMode ? 'bg-red-400' : 'bg-red-500'} flex-shrink-0 mt-2`}></div>
                                <span className={`${darkMode ? 'text-gray-200' : 'text-gray-700'} text-xs sm:text-sm leading-snug`}>{benefit}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                        
                        {/* Academic Support */}
                        <div className={`p-3 sm:p-4 rounded-xl ${darkMode ? 'bg-indigo-500/10 border-indigo-400/20' : 'bg-indigo-50/50 border-indigo-200/30'} backdrop-blur-sm border`}>
                          <h4 className={`text-base sm:text-lg font-semibold mb-2 ${darkMode ? 'text-indigo-300' : 'text-indigo-600'} flex items-center gap-2`}>
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.746 0 3.332.477 4.5 1.253v13C19.832 18.477 18.246 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                            </svg>
                            Academic Support & Resources
                          </h4>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-1 sm:gap-2">
                            {(whyChooseData.academicSupport || []).map((support, index) => (
                              <div key={index} className="flex items-start gap-2 p-1.5 rounded-lg hover:bg-indigo-500/5 transition-colors">
                                <div className={`w-1.5 h-1.5 rounded-full ${darkMode ? 'bg-indigo-400' : 'bg-indigo-500'} flex-shrink-0 mt-2`}></div>
                                <span className={`${darkMode ? 'text-gray-200' : 'text-gray-700'} text-xs sm:text-sm leading-snug`}>{support}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                        
                        {/* Financial Advantages */}
                        <div className={`p-3 sm:p-4 rounded-xl ${darkMode ? 'bg-gradient-to-r from-green-500/10 to-emerald-500/10 border-green-400/20' : 'bg-gradient-to-r from-green-500/5 to-emerald-500/5 border-green-400/15'} backdrop-blur-sm border`}>
                          <h4 className={`text-base sm:text-lg font-semibold mb-2 ${darkMode ? 'text-green-300' : 'text-green-600'} flex items-center gap-2`}>
                            <svg className="w-5 h-5 sm:w-6 sm:h-6 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                            Financial Advantages
                          </h4>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-1 sm:gap-2">
                            {(whyChooseData.financialAdvantages || []).map((advantage, index) => (
                              <div key={index} className="flex items-start gap-2 p-1.5 rounded-lg hover:bg-green-500/5 transition-colors">
                                <div className={`w-1.5 h-1.5 rounded-full ${darkMode ? 'bg-green-400' : 'bg-green-500'} flex-shrink-0 mt-2`}></div>
                                <span className={`${darkMode ? 'text-gray-200' : 'text-gray-700'} text-xs sm:text-sm leading-snug`}>{advantage}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })()}

              {/* University Content - Full Width Vertical Stack */}
              <div className={`rounded-2xl backdrop-blur-xl ${darkMode ? 'bg-gray-800/50 border-gray-600/30' : 'bg-white/80 border-gray-200/30'} border shadow-2xl mb-6 sm:mb-8 overflow-hidden`}>
                <div className="p-4 sm:p-6 lg:p-8 space-y-8">
                  
                  {/* Main Photo and Slider Section - Side by Side on Large Screens */}
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-8">
                    
                    {/* Main Photo Section */}
                    <div>
                      {(() => {
                        const images = getUniversityImages(selectedUniversity.name);
                        
                        if (images) {
                          return (
                            <div>
                              {/* Main Profile Image */}
                              <div className="relative w-full pb-[56.25%] h-0 overflow-hidden rounded-2xl shadow-2xl group">
                              <img 
                                src={images.profile} 
                                alt={`${selectedUniversity.name} Profile`}
                                className="absolute top-0 left-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                                onLoad={() => {/* Profile loaded */}}
                                onError={(e) => {
                                  console.log('Profile image failed to load:', images.profile);
                                  e.target.style.display = 'none';
                                  e.target.nextSibling.style.display = 'flex';
                                }}
                              />
                              {/* Fallback placeholder */}
                              <div className="absolute inset-0 w-full h-full flex items-center justify-center bg-gradient-to-br from-gray-600/80 to-gray-800/80 backdrop-blur-sm" style={{display: 'none'}}>
                                <div className="text-center">
                                  <div className="p-4 rounded-2xl bg-indigo-500/20 backdrop-blur-sm border border-indigo-400/30 mx-auto mb-4 w-16 h-16 flex items-center justify-center">
                                    {UniversityIcons[selectedUniversity.icon] && 
                                      React.createElement(UniversityIcons[selectedUniversity.icon], { size: "32" })}
                                  </div>
                                  <p className="text-lg font-bold text-white mb-2">University Image</p>
                                  <p className="text-sm text-gray-300">Coming Soon</p>
                                </div>
                              </div>
                              <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent"></div>
                              <div className="absolute bottom-0 left-0 right-0 p-4">
                                <div className="flex items-center gap-2">
                                  <div className="p-1.5 rounded-lg bg-red-500/20 backdrop-blur-sm">
                                    <MatrixLocation size="14" className="matrix-emoji-red" />
                                  </div>
                                  <p className="text-white text-sm sm:text-base font-semibold">
                                    {selectedUniversity.location}
                                  </p>
                                </div>
                              </div>
                            </div>
                          </div>
                        );
                      } else {
                        // Fallback for universities without images
                        return (
                          <div className="relative rounded-2xl overflow-hidden shadow-2xl bg-gradient-to-br from-gray-600/80 to-gray-800/80 backdrop-blur-sm">
                            <div className="w-full h-56 sm:h-64 lg:h-56 flex items-center justify-center">
                              <div className="text-center">
                                <div className="p-6 rounded-2xl bg-indigo-500/20 backdrop-blur-sm border border-indigo-400/30 mx-auto mb-6 w-20 h-20 flex items-center justify-center">
                                  {UniversityIcons[selectedUniversity.icon] && 
                                    React.createElement(UniversityIcons[selectedUniversity.icon], { size: "40" })}
                                </div>
                                <p className="text-xl font-bold text-white mb-3">University Image</p>
                                <p className="text-sm text-gray-300">Coming Soon</p>
                              </div>
                            </div>
                            <div className="absolute bottom-0 left-0 right-0 p-4 bg-gradient-to-t from-black/60 to-transparent">
                              <div className="flex items-center gap-2">
                                <div className="p-1.5 rounded-lg bg-red-500/20 backdrop-blur-sm">
                                  <MatrixLocation size="14" className="matrix-emoji-red" />
                                </div>
                                <p className="text-white text-sm sm:text-base font-semibold">
                                  {selectedUniversity.location}
                                </p>
                              </div>
                            </div>
                          </div>
                        );
                      }
                    })()}
                    </div>
                    
                    {/* Slider Photos Section */}
                    <div>
                      {(() => {
                        const images = getUniversityImages(selectedUniversity.name);
                        
                        if (images && images.gallery && images.gallery.length > 0) {
                          return (
                            <div>
                              {/* Image Gallery Slider */}
                              <div className="relative">
                                <div 
                                  className="relative w-full pb-[56.25%] h-0 overflow-hidden rounded-2xl shadow-xl cursor-grab active:cursor-grabbing select-none group"
                                onTouchStart={onTouchStart}
                                onTouchMove={onTouchMove}
                                onTouchEnd={() => onTouchEnd(images)}
                                onMouseDown={onMouseDown}
                                onMouseMove={onMouseMove}
                                onMouseUp={() => onMouseUp(images)}
                                onMouseLeave={onMouseLeave}
                                style={{ 
                                  touchAction: 'manipulation',
                                  userSelect: 'none',
                                  WebkitUserSelect: 'none'
                                }}
                              >
                                {/* Images Container with Sliding Effect */}
                                <div className="absolute top-0 left-0 w-full h-full overflow-hidden">
                                  {images.gallery.map((imageSrc, index) => (
                                    <div 
                                      key={index} 
                                      className="absolute top-0 w-full h-full transition-transform duration-500 ease-out"
                                      style={{ 
                                        left: `${index * 100}%`,
                                        transform: `translateX(-${currentImageIndex * 100}%)`,
                                        willChange: 'transform'
                                      }}
                                    >
                                      <img 
                                        src={imageSrc} 
                                        alt={`${selectedUniversity.name} Gallery ${index + 1}`}
                                        className="w-full h-full object-cover"
                                        draggable="false"
                                        loading="eager"
                                        style={{ 
                                          userSelect: 'none',
                                          WebkitUserSelect: 'none',
                                          pointerEvents: 'none'
                                        }}
                                        onLoad={() => {
                                          setImageLoadingStates(prev => ({...prev, [index]: 'loaded'}));
                                        }}
                                        onError={(e) => {
                                          console.error(`❌ Image ${index + 1} FAILED to load:`, imageSrc);
                                          setImageLoadingStates(prev => ({...prev, [index]: 'error'}));
                                        }}
                                      />
                                      {/* Loading or Error placeholder */}
                                      {imageLoadingStates[index] !== 'loaded' && (
                                        <div className="absolute inset-0 flex items-center justify-center bg-gradient-to-br from-gray-600/80 to-gray-800/80 backdrop-blur-sm">
                                          <div className="text-center">
                                            {imageLoadingStates[index] === 'error' ? (
                                              <div>
                                                <div className="text-3xl sm:text-4xl mb-2">🏛️</div>
                                                <p className="text-sm font-medium text-white">University Image</p>
                                                <p className="text-xs text-gray-300">Image {index + 1}</p>
                                              </div>
                                            ) : (
                                              <div>
                                                <div className="w-6 h-6 border-2 border-indigo-400 border-t-transparent rounded-full animate-spin mx-auto mb-2"></div>
                                                <p className="text-xs text-gray-300">Loading...</p>
                                              </div>
                                            )}
                                          </div>
                                        </div>
                                      )}
                                    </div>
                                  ))}
                                </div>

                                {/* Navigation Arrows */}
                                <button
                                  onClick={() => {
                                    if (!isTransitioning && currentImageIndex > 0) {
                                      setIsTransitioning(true);
                                      setCurrentImageIndex(prev => prev - 1);
                                      setTimeout(() => setIsTransitioning(false), 50);
                                    }
                                  }}
                                  className={`absolute left-2 top-1/2 transform -translate-y-1/2 p-2 rounded-full bg-black/50 hover:bg-black/70 text-white transition-all duration-300 z-10 backdrop-blur-sm border border-white/20 opacity-0 group-hover:opacity-100 active:scale-95 ${
                                    currentImageIndex === 0 ? 'cursor-not-allowed opacity-30' : ''
                                  }`}
                                >
                                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                                  </svg>
                                </button>
                                <button
                                  onClick={() => {
                                    if (!isTransitioning && currentImageIndex < images.gallery.length - 1) {
                                      setIsTransitioning(true);
                                      setCurrentImageIndex(prev => prev + 1);
                                      setTimeout(() => setIsTransitioning(false), 50);
                                    }
                                  }}
                                  className={`absolute right-2 top-1/2 transform -translate-y-1/2 p-2 rounded-full bg-black/50 hover:bg-black/70 text-white transition-all duration-300 z-10 backdrop-blur-sm border border-white/20 opacity-0 group-hover:opacity-100 active:scale-95 ${
                                    currentImageIndex === images.gallery.length - 1 ? 'cursor-not-allowed opacity-30' : ''
                                  }`}
                                >
                                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M14 5l7 7m0 0l-7 7m7-7H3" />
                                  </svg>
                                </button>
                                
                                {/* Image Counter */}
                                <div className="absolute bottom-3 right-3 px-3 py-1.5 bg-black/60 text-white text-xs rounded-full backdrop-blur-sm border border-white/20">
                                  {currentImageIndex + 1} / {images.gallery.length}
                                </div>
                              </div>

                              {/* Thumbnail Dots */}
                              <div className="flex justify-center mt-3 space-x-2">
                                {images.gallery.map((_, index) => (
                                  <button
                                    key={index}
                                    onClick={() => {
                                      if (!isTransitioning && index !== currentImageIndex) {
                                        setIsTransitioning(true);
                                        setCurrentImageIndex(index);
                                        setTimeout(() => setIsTransitioning(false), 50);
                                      }
                                    }}
                                    className={`w-2.5 h-2.5 rounded-full transition-all duration-300 active:scale-110 ${
                                      index === currentImageIndex 
                                        ? 'bg-indigo-400 scale-125' 
                                        : 'bg-gray-400 hover:bg-gray-300'
                                    }`}
                                  />
                                ))}
                              </div>
                            </div>
                          </div>
                        );
                      }
                      return null;
                    })()}
                    </div>
                    
                  </div>
                  {/* End of Main Photo and Slider Grid */}

                  {/* About Section */}
                  <div>
                    <h3 className="text-xl sm:text-2xl font-bold mb-4 flex items-center gap-3 bg-gradient-to-r from-yellow-400 to-orange-400 bg-clip-text text-transparent">
                      <div className={`p-2 rounded-xl ${darkMode ? 'bg-yellow-500/20 border-yellow-400/30' : 'bg-yellow-500/10 border-yellow-400/20'} backdrop-blur-sm border`}>
                        <svg className="w-5 h-5 text-yellow-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                      </div>
                      About {selectedUniversity.name}
                    </h3>
                    <p className={`text-sm sm:text-base leading-relaxed ${darkMode ? 'text-gray-200' : 'text-gray-700'} mb-6`}>
                      {selectedUniversity.description}
                    </p>
                  </div>
                  
                  {/* Map Section */}
                  <div>
                    {selectedUniversity.mapUrl && (
                      <div className={`p-4 sm:p-6 rounded-xl ${darkMode ? 'bg-gray-700/30 border-gray-600/30' : 'bg-gray-50/50 border-gray-200/30'} backdrop-blur-sm border`}>
                        <h4 className={`text-lg sm:text-xl font-semibold mb-4 ${darkMode ? 'text-gray-200' : 'text-gray-800'} flex items-center gap-3`}>
                          <div className={`p-2 rounded-xl ${darkMode ? 'bg-red-500/20 border-red-400/30' : 'bg-red-500/10 border-red-400/20'} backdrop-blur-sm border`}>
                            <svg className="w-5 h-5 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                            </svg>
                          </div>
                          Location on Map
                        </h4>
                        <div className="relative w-full">
                        <div className="relative w-full pb-[56.25%] h-0 overflow-hidden rounded-2xl shadow-xl backdrop-blur-sm border border-white/20 hover:shadow-2xl transition-all duration-300">
                            <iframe
                              src={`${selectedUniversity.mapUrl}&zoom=16&maptype=satellite&gestureHandling=greedy`}
                              className="absolute top-0 left-0 w-full h-full transition-all duration-300 hover:scale-[1.02]"
                              style={{ 
                                border: 0,
                                borderRadius: '1rem'
                              }}
                              allowFullScreen=""
                              loading="lazy"
                              referrerPolicy="no-referrer-when-downgrade"
                              title={`Location of ${selectedUniversity.name}`}
                            />
                          </div>
                        </div>
                        <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'} mt-4 text-center font-medium flex items-center justify-center gap-2`}>
                          <svg className="w-4 h-4 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                          </svg>
                          Interactive Map • Scroll/Touch to zoom in and explore
                        </p>
                      </div>
                    )}
                  </div>

                </div>
              </div>

              {/* Complete Fee Breakdown */}
              <div className="grid grid-cols-1 gap-4 sm:gap-6 mb-4 sm:mb-6">
                {/* 1st Year Complete Breakdown */}
                <div className={`rounded-xl backdrop-blur-xl ${darkMode ? 'bg-gray-800/50 border-gray-600/30' : 'bg-white/80 border-gray-200/30'} border shadow-xl overflow-hidden`}>
                  <div className="p-3 sm:p-4 lg:p-6">
                    <h3 className="text-lg sm:text-xl lg:text-2xl font-bold mb-4 sm:mb-6 bg-gradient-to-r from-yellow-400 to-orange-400 bg-clip-text text-transparent">
                      Complete 1st Year Fee Breakdown
                    </h3>
                    
                    <div className="space-y-4 sm:space-y-6">
                      {/* Basic Fees */}
                      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
                        <div className="space-y-3">
                          <h4 className="text-base sm:text-lg font-bold mb-3 flex items-center gap-2 bg-gradient-to-r from-indigo-400 to-purple-400 bg-clip-text text-transparent">
                            <div className={`p-1.5 rounded-lg ${darkMode ? 'bg-indigo-500/20 border-indigo-400/30' : 'bg-indigo-500/10 border-indigo-400/20'} backdrop-blur-sm border`}>
                              <svg className="w-4 h-4 text-indigo-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                              </svg>
                            </div>
                            Academic Fees
                          </h4>
                          <div className="space-y-2">
                            <div className={`flex justify-between items-center p-2.5 rounded-lg ${darkMode ? 'bg-gray-700/30 border-gray-600/20' : 'bg-gray-50/80 border-gray-200/30'} backdrop-blur-sm border`}>
                              <span className={`text-xs sm:text-sm ${darkMode ? 'text-gray-200' : 'text-gray-700'}`}>Tuition Fee:</span>
                              <div className="text-right">
                                <span className={`font-bold text-blue-400 text-xs sm:text-sm whitespace-nowrap`}>{formatFeeWithInr(selectedUniversity.tuition)}</span>
                                <div className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>(Per Year)</div>
                              </div>
                            </div>
                            <div className={`flex justify-between items-center p-2.5 rounded-lg ${darkMode ? 'bg-gray-700/30 border-gray-600/20' : 'bg-gray-50/80 border-gray-200/30'} backdrop-blur-sm border`}>
                              <span className={`text-xs sm:text-sm ${darkMode ? 'text-gray-200' : 'text-gray-700'}`}>Hostel Fee:</span>
                              <div className="text-right">
                                <span className={`font-bold text-blue-400 text-xs sm:text-sm whitespace-nowrap`}>{formatFeeWithInr(selectedUniversity.hostel)}</span>
                                <div className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>(Per Year)</div>
                              </div>
                            </div>
                          </div>
                        </div>
                        
                        <div className="space-y-3">
                          <h4 className="text-base sm:text-lg font-bold mb-3 flex items-center gap-2 bg-gradient-to-r from-blue-400 to-cyan-400 bg-clip-text text-transparent">
                            <div className={`p-1.5 rounded-lg ${darkMode ? 'bg-blue-500/20 border-blue-400/30' : 'bg-blue-500/10 border-blue-400/20'} backdrop-blur-sm border`}>
                              <svg className="w-4 h-4 text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2m2 4h10a2 2 0 002-2v-6a2 2 0 00-2-2H9a2 2 0 00-2 2v2a2 2 0 002 2zm7-5a2 2 0 11-4 0 2 2 0 014 0z" />
                              </svg>
                            </div>
                            <div>
                              Additional Charges
                              <div className={`text-xs ${darkMode ? 'text-blue-300' : 'text-blue-500'} font-normal`}>only for (1st Year)</div>
                            </div>
                          </h4>
                          <div className="space-y-2">
                            <div className={`flex justify-between items-start p-2.5 rounded-lg ${darkMode ? 'bg-gray-700/30 border-gray-600/20' : 'bg-gray-50/80 border-gray-200/30'} backdrop-blur-sm border`}>
                              <span className={`text-xs sm:text-sm ${darkMode ? 'text-gray-200' : 'text-gray-700'} flex-1 pr-2`}>Flight Tickets (Delhi/Bangalore to Russia):</span>
                              <div className="text-right">
                                <div className="font-bold text-red-400 text-xs sm:text-sm whitespace-nowrap">₹65,000</div>
                                <div className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>(Purchased in India)</div>
                              </div>
                            </div>
                            <div className={`flex justify-between items-center p-2.5 rounded-lg ${darkMode ? 'bg-gray-700/30 border-gray-600/20' : 'bg-gray-50/80 border-gray-200/30'} backdrop-blur-sm border`}>
                              <span className={`text-xs sm:text-sm ${darkMode ? 'text-gray-200' : 'text-gray-700'}`}>Medical Checkup:</span>
                              <div className="text-right">
                                <div className={`font-bold ${darkMode ? 'text-gray-300' : 'text-gray-600'} text-xs sm:text-sm whitespace-nowrap`}>8,000 RUB</div>
                                <div className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>(₹{Math.round(8000 * (parseFloat(liveExchangeRates.rubToInr) || 1.10)).toLocaleString('en-IN')})</div>
                              </div>
                            </div>
                            <div className={`flex justify-between items-center p-2.5 rounded-lg ${darkMode ? 'bg-gray-700/30 border-gray-600/20' : 'bg-gray-50/80 border-gray-200/30'} backdrop-blur-sm border`}>
                              <span className={`text-xs sm:text-sm ${darkMode ? 'text-gray-200' : 'text-gray-700'}`}>Insurance:</span>
                              <div className="text-right">
                                <div className={`font-bold ${darkMode ? 'text-gray-300' : 'text-gray-600'} text-xs sm:text-sm whitespace-nowrap`}>7,500 RUB</div>
                                <div className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>(₹{Math.round(7500 * (parseFloat(liveExchangeRates.rubToInr) || 1.10)).toLocaleString('en-IN')})</div>
                              </div>
                            </div>
                            <div className={`flex justify-between items-center p-2.5 rounded-lg ${darkMode ? 'bg-gray-700/30 border-gray-600/20' : 'bg-gray-50/80 border-gray-200/30'} backdrop-blur-sm border`}>
                              <span className={`text-xs sm:text-sm ${darkMode ? 'text-gray-200' : 'text-gray-700'}`}>Documentation & Visa:</span>
                              <div className="text-right">
                                <div className={`font-bold ${darkMode ? 'text-gray-300' : 'text-gray-600'} text-xs sm:text-sm whitespace-nowrap`}>21,000 RUB</div>
                                <div className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>(₹{Math.round(21000 * (parseFloat(liveExchangeRates.rubToInr) || 1.10)).toLocaleString('en-IN')})</div>
                              </div>
                            </div>
                            <div className={`flex justify-between items-center p-2.5 rounded-lg ${darkMode ? 'bg-gray-700/30 border-gray-600/20' : 'bg-gray-50/80 border-gray-200/30'} backdrop-blur-sm border`}>
                              <span className={`text-xs sm:text-sm ${darkMode ? 'text-gray-200' : 'text-gray-700'}`}>Administrative (OTC):</span>
                              <div className="text-right">
                                <div className={`font-bold ${darkMode ? 'text-gray-300' : 'text-gray-600'} text-xs sm:text-sm whitespace-nowrap`}>63,700 RUB</div>
                                <div className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>(₹{Math.round(63700 * (parseFloat(liveExchangeRates.rubToInr) || 1.10)).toLocaleString('en-IN')})</div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Total Calculations with Detailed Breakdown */}
                      <div className={`border-t ${darkMode ? 'border-gray-600/30' : 'border-gray-300/30'} pt-4 sm:pt-6`}>
                        {(() => {
                          const calculation = calculateTotalFirstYear(selectedUniversity);
                          return (
                            <div className="space-y-4 sm:space-y-6">
                              {/* Calculation Summary */}
                              <div className={`rounded-xl backdrop-blur-xl ${darkMode ? 'bg-blue-500/10 border-blue-400/30' : 'bg-blue-500/5 border-blue-400/20'} border p-3 sm:p-4`}>
                                <h4 className="text-base sm:text-lg font-bold mb-3 sm:mb-4 bg-gradient-to-r from-blue-400 to-cyan-400 bg-clip-text text-transparent">
                                  Complete Fee Calculation (1st Year)
                                </h4>
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-xs sm:text-sm">
                                  <div className="space-y-2">
                                    <div className={`flex justify-between p-2.5 rounded-lg ${darkMode ? 'bg-gradient-to-r from-blue-500/20 to-indigo-500/10 border-blue-400/30' : 'bg-gradient-to-r from-blue-500/10 to-indigo-500/5 border-blue-400/20'} backdrop-blur-sm border shadow-lg`}>
                                      <span className={`${darkMode ? 'text-blue-200' : 'text-blue-700'} font-medium`}>Tuition Fee:</span>
                                      <div className="text-right">
                                        <div className="font-bold text-blue-400 whitespace-nowrap text-xs sm:text-sm">{(calculation.breakdown.tuition || 0).toLocaleString('en-IN')} RUB</div>
                                        <div className={`text-xs ${darkMode ? 'text-blue-300' : 'text-blue-500'}`}>(₹{Math.round((calculation.breakdown.tuition || 0) * 1.10).toLocaleString('en-IN')})</div>
                                      </div>
                                    </div>
                                    <div className={`flex justify-between p-2.5 rounded-lg ${darkMode ? 'bg-gradient-to-r from-purple-500/20 to-violet-500/10 border-purple-400/30' : 'bg-gradient-to-r from-purple-500/10 to-violet-500/5 border-purple-400/20'} backdrop-blur-sm border shadow-lg`}>
                                      <span className={`${darkMode ? 'text-purple-200' : 'text-purple-700'} font-medium`}>Hostel Fee:</span>
                                      <div className="text-right">
                                        <div className="font-bold text-purple-400 whitespace-nowrap text-xs sm:text-sm">{(calculation.breakdown.hostelRUB || 0).toLocaleString('en-IN')} RUB</div>
                                        <div className={`text-xs ${darkMode ? 'text-purple-300' : 'text-purple-500'}`}>(₹{Math.round((calculation.breakdown.hostelRUB || 0) * 1.10).toLocaleString('en-IN')})</div>
                                      </div>
                                    </div>
                                    <div className={`flex justify-between p-2.5 rounded-lg ${darkMode ? 'bg-gradient-to-r from-red-500/20 to-pink-500/10 border-red-400/30' : 'bg-gradient-to-r from-red-500/10 to-pink-500/5 border-red-400/20'} backdrop-blur-sm border shadow-lg`}>
                                      <span className={`${darkMode ? 'text-red-200' : 'text-red-700'} font-medium`}>Flight Tickets:</span>
                                      <div className="text-right">
                                        <div className="font-bold text-red-400 whitespace-nowrap">₹{(calculation.breakdown.flightTicketINR || 0).toLocaleString('en-IN')}</div>
                                        <div className={`text-xs ${darkMode ? 'text-red-300' : 'text-red-500'}`}>(Purchased in India)</div>
                                      </div>
                                    </div>
                                  </div>
                                  <div className="space-y-3">
                                    <div className={`flex justify-between p-3 rounded-xl ${darkMode ? 'bg-gradient-to-r from-green-500/20 to-emerald-500/10 border-green-400/30' : 'bg-gradient-to-r from-green-500/10 to-emerald-500/5 border-green-400/20'} backdrop-blur-sm border shadow-lg`}>
                                      <span className={`${darkMode ? 'text-green-200' : 'text-green-700'} font-medium`}>Medical & Insurance:</span>
                                      <div className="text-right">
                                        <div className={`font-bold ${darkMode ? 'text-green-300' : 'text-green-600'} whitespace-nowrap`}>15,500 RUB</div>
                                        <div className={`text-xs ${darkMode ? 'text-green-400' : 'text-green-500'}`}>(₹17,050)</div>
                                      </div>
                                    </div>
                                    <div className={`flex justify-between p-3 rounded-xl ${darkMode ? 'bg-gradient-to-r from-orange-500/20 to-amber-500/10 border-orange-400/30' : 'bg-gradient-to-r from-orange-500/10 to-amber-500/5 border-orange-400/20'} backdrop-blur-sm border shadow-lg`}>
                                      <span className={`${darkMode ? 'text-orange-200' : 'text-orange-700'} font-medium`}>Documentation & Visa:</span>
                                      <div className="text-right">
                                        <div className={`font-bold ${darkMode ? 'text-orange-300' : 'text-orange-600'} whitespace-nowrap`}>21,000 RUB</div>
                                        <div className={`text-xs ${darkMode ? 'text-orange-400' : 'text-orange-500'}`}>(₹23,100)</div>
                                      </div>
                                    </div>
                                    <div className={`flex justify-between p-3 rounded-xl ${darkMode ? 'bg-gradient-to-r from-cyan-500/20 to-teal-500/10 border-cyan-400/30' : 'bg-gradient-to-r from-cyan-500/10 to-teal-500/5 border-cyan-400/20'} backdrop-blur-sm border shadow-lg`}>
                                      <span className={`${darkMode ? 'text-cyan-200' : 'text-cyan-700'} font-medium`}>Administrative (OTC):</span>
                                      <div className="text-right">
                                        <div className={`font-bold ${darkMode ? 'text-cyan-300' : 'text-cyan-600'} whitespace-nowrap`}>{(calculation.breakdown.administrativeRUB || 0).toLocaleString('en-IN')} RUB</div>
                                        <div className={`text-xs ${darkMode ? 'text-cyan-400' : 'text-cyan-500'}`}>(₹{Math.round((calculation.breakdown.administrativeRUB || 0) * (parseFloat(liveExchangeRates.rubToInr) || 1.10)).toLocaleString('en-IN')})</div>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                                <div className={`border-t ${darkMode ? 'border-gray-600/30' : 'border-gray-300/30'} pt-3 mt-4`}>
                                  {/* Horizontal line above Grand Total */}
                                  <div className={`border-t-2 ${darkMode ? 'border-green-400/40' : 'border-green-500/40'} mb-3`}></div>
                                  <div className={`p-3 sm:p-4 rounded-lg ${darkMode ? 'bg-green-500/20 border-green-400/30' : 'bg-green-500/10 border-green-400/20'} backdrop-blur-sm border text-center`}>
                                    <div className={`text-xs sm:text-sm ${darkMode ? 'text-green-300' : 'text-green-600'} mb-2`}>Grand Total:</div>
                                    <div className={`text-sm sm:text-lg lg:text-xl font-bold ${darkMode ? 'text-green-400' : 'text-green-600'} break-words`}>
                                      {calculation.totalRUB} RUB + ₹65,000 rupees
                                    </div>
                                    <div className={`text-xs sm:text-base lg:text-lg font-semibold ${darkMode ? 'text-green-300' : 'text-green-500'} mt-1 break-words`}>
                                      (₹{(calculation.breakdown.totalINR - 65000).toLocaleString('en-IN')}+₹65,000={calculation.totalINR})
                                    </div>
                                  </div>
                                </div>
                              </div>

                              {/* Final Total Display */}
                              <div className={`rounded-xl backdrop-blur-xl ${darkMode ? 'bg-gradient-to-br from-green-500/20 to-emerald-500/10 border-green-400/30' : 'bg-gradient-to-br from-green-500/10 to-emerald-500/5 border-green-400/20'} border p-3 sm:p-4 lg:p-6 text-center shadow-lg`}>
                                <h4 className="text-sm sm:text-lg lg:text-xl font-bold mb-2 sm:mb-3 lg:mb-4 bg-gradient-to-r from-green-400 to-emerald-400 bg-clip-text text-transparent">
                                  Complete 1st Year Investment (All-Inclusive)
                                </h4>
                                
                                {/* Main Amount Display */}
                                <div className="space-y-2 sm:space-y-3 mb-2 sm:mb-3 lg:mb-4">
                                  {/* INR Display with Live Rate Indicator */}
                                  <div className={`p-2 sm:p-3 rounded-lg ${darkMode ? 'bg-green-500/10 border-green-400/20' : 'bg-green-500/5 border-green-400/15'} border backdrop-blur-sm`}>
                                    {exchangeRateLoading ? (
                                      <div className="flex items-center justify-center gap-2">
                                        <div className="w-3 h-3 sm:w-4 sm:h-4 border-2 border-t-transparent border-gray-400 rounded-full animate-spin"></div>
                                        <span className={`text-xs sm:text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Loading live rates...</span>
                                      </div>
                                    ) : (
                                      <div className="space-y-1">
                                        <div className="flex flex-col items-center justify-center gap-1">
                                          <div className="flex items-center gap-1 sm:gap-2">
                                            <div className="w-1.5 h-1.5 sm:w-2 sm:h-2 bg-green-400 rounded-full animate-pulse"></div>
                                            <span className={`text-xs sm:text-sm lg:text-base font-bold ${darkMode ? 'text-green-300' : 'text-green-600'} text-center`}>
                                              {liveExchangeRates.isLive ? 'Live: ' : 'Approx: '}{calculation.totalINR}
                                            </span>
                                          </div>
                                          <div className={`text-xs ${darkMode ? 'text-green-400/80' : 'text-green-600/80'} text-center`}>
                                            (₹{(calculation.breakdown.totalINR - 65000).toLocaleString('en-IN')}+₹65,000 flight ticket)
                                          </div>
                                        </div>
                                        {liveExchangeRates.isLive && (
                                          <div className="space-y-1">
                                            <div className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'} flex flex-col items-center justify-center gap-1`}>
                                              <span className="font-medium">Rate: 1 RUB = ₹{liveExchangeRates.rubToInr}</span>
                                              <div className="flex items-center gap-1">
                                                <span className="text-gray-400">•</span>
                                                <span className="text-xs">Updated: {liveExchangeRates.lastUpdated}</span>
                                              </div>
                                            </div>
                                          </div>
                                        )}
                                      </div>
                                    )}
                                  </div>
                                </div>
                                
                                {/* Includes Section */}
                                <div className={`inline-flex items-center gap-1 sm:gap-2 px-2 sm:px-3 lg:px-4 py-1.5 sm:py-2 rounded-full ${darkMode ? 'bg-green-500/20 text-green-300 border-green-400/30' : 'bg-green-500/10 text-green-600 border-green-400/20'} text-xs sm:text-sm font-medium border shadow-sm`}>
                                  <svg className="w-3 h-3 sm:w-4 sm:h-4 lg:w-5 lg:h-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                                  </svg>
                                  <span className="text-center">
                                    <span className="font-semibold">Includes:</span> <span className="hidden xs:inline">Flight Tickets + Tuition + Hostel + All Charges</span><span className="xs:hidden">All Fees</span>
                                  </span>
                                </div>
                              </div>

                              <div className={`rounded-xl ${darkMode ? 'bg-yellow-500/10 border-yellow-400/30' : 'bg-yellow-500/5 border-yellow-400/20'} border p-3 sm:p-4 text-center backdrop-blur-sm`}>
                                <div className="flex items-center justify-center gap-2 mb-2 sm:mb-3">
                                  <svg className="w-4 h-4 sm:w-5 sm:h-5 text-yellow-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                                  </svg>
                                  <span className="text-yellow-400 font-semibold text-sm sm:text-base">Important Note</span>
                                </div>
                                <p className={`text-xs sm:text-sm lg:text-base ${darkMode ? 'text-gray-300' : 'text-gray-600'} leading-relaxed`}>
                                  This is the COMPLETE amount parents must budget for 1st year - includes flight tickets, all charges & fees!
                                </p>
                              </div>
                            </div>
                          );
                        })()}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Annual Fees Breakdown */}
                <div className={`rounded-2xl backdrop-blur-xl ${darkMode ? 'bg-gray-800/50 border-gray-600/30' : 'bg-white/80 border-gray-200/30'} border shadow-2xl overflow-hidden`}>
                  <div className="p-4 sm:p-6 lg:p-8">
                    <h3 className="text-xl sm:text-2xl font-bold mb-6 sm:mb-8 bg-gradient-to-r from-purple-400 to-pink-400 bg-clip-text text-transparent">
                      From 2nd year to 6th year Annual Fees (Budget Planning)
                    </h3>
                    
                    <div className="space-y-4">
                      <h4 className="text-lg font-bold mb-4 flex items-center gap-3 bg-gradient-to-r from-purple-400 to-indigo-400 bg-clip-text text-transparent">
                        <div className={`p-2 rounded-xl ${darkMode ? 'bg-purple-500/20 border-purple-400/30' : 'bg-purple-500/10 border-purple-400/20'} backdrop-blur-sm border`}>
                          <svg className="w-5 h-5 text-purple-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2m2 4h10a2 2 0 002-2v-6a2 2 0 00-2-2H9a2 2 0 00-2 2v6a2 2 0 002 2zm7-5a2 2 0 11-4 0 2 2 0 014 0z" />
                          </svg>
                        </div>
                        Annual Fees (From 2nd year to 6th year)
                      </h4>
                      <div className="space-y-3">
                        <div className={`flex justify-between items-center p-3 rounded-xl ${darkMode ? 'bg-gray-700/30 border-gray-600/20' : 'bg-gray-50/80 border-gray-200/30'} backdrop-blur-sm border`}>
                          <span className={`text-sm sm:text-base ${darkMode ? 'text-gray-200' : 'text-gray-700'}`}>Tuition Fee:</span>
                          <div className="text-right">
                            <span className={`font-bold text-purple-400 text-sm sm:text-base text-right whitespace-nowrap`}>{formatFeeWithInr(selectedUniversity.tuition)}</span>
                            <div className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>(per year)</div>
                          </div>
                        </div>
                        <div className={`flex justify-between items-center p-3 rounded-xl ${darkMode ? 'bg-gray-700/30 border-gray-600/20' : 'bg-gray-50/80 border-gray-200/30'} backdrop-blur-sm border`}>
                          <span className={`text-sm sm:text-base ${darkMode ? 'text-gray-200' : 'text-gray-700'}`}>Hostel Fee:</span>
                          <div className="text-right">
                            <span className={`font-bold text-purple-400 text-sm sm:text-base text-right whitespace-nowrap`}>{formatFeeWithInr(selectedUniversity.hostel)}</span>
                            <div className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>(per year)</div>
                          </div>
                        </div>
                        <div className={`flex justify-between items-center p-3 rounded-xl ${darkMode ? 'bg-gray-700/30 border-gray-600/20' : 'bg-gray-50/80 border-gray-200/30'} backdrop-blur-sm border`}>
                          <span className={`text-sm sm:text-base ${darkMode ? 'text-gray-200' : 'text-gray-700'}`}>Insurance:</span>
                          <div className="text-right">
                            <div className={`font-bold ${darkMode ? 'text-gray-300' : 'text-gray-600'} text-sm sm:text-base whitespace-nowrap`}>7,500 RUB</div>
                            <div className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>(₹8,250 per year)</div>
                          </div>
                        </div>
                        <div className={`flex justify-between items-center p-3 rounded-xl ${darkMode ? 'bg-gray-700/30 border-gray-600/20' : 'bg-gray-50/80 border-gray-200/30'} backdrop-blur-sm border`}>
                          <span className={`text-sm sm:text-base ${darkMode ? 'text-gray-200' : 'text-gray-700'}`}>Visa Extension:</span>
                          <div className="text-right">
                            <div className={`font-bold ${darkMode ? 'text-gray-300' : 'text-gray-600'} text-sm sm:text-base whitespace-nowrap`}>2,000 RUB</div>
                            <div className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>(₹2,200 when applicable)</div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Complete 6-Year MBBS Budget Planning */}
                <div className={`rounded-2xl backdrop-blur-xl ${darkMode ? 'bg-gray-800/50 border-gray-600/30' : 'bg-white/80 border-gray-200/30'} border shadow-2xl overflow-hidden`}>
                  <div className="p-4 sm:p-6 lg:p-8">
                    <h3 className="text-xl sm:text-2xl font-bold mb-6 sm:mb-8 bg-gradient-to-r from-blue-400 to-cyan-400 bg-clip-text text-transparent">
                      Complete 6-Year MBBS Budget Planning
                    </h3>
                    
                    <div className={`rounded-2xl backdrop-blur-xl ${darkMode ? 'bg-blue-500/10 border-blue-400/30' : 'bg-blue-500/5 border-blue-400/20'} border p-4 sm:p-6`}>
                      <div className="space-y-3 text-sm sm:text-base">
                        {(() => {
                          const calculation = calculateTotalFirstYear(selectedUniversity);
                          // Calculate annual fees from 2nd year to 6th year (no flight tickets, no one-time charges)
                          const tuition = parseFloat(selectedUniversity.tuition.replace(/[^\d]/g, '')) || 0;
                          let hostelRUB = 0;
                          if (selectedUniversity.hostel.includes('₹')) {
                            let amount = selectedUniversity.hostel.replace(/[₹,]/g, '');
                            if (amount.includes('-')) {
                              amount = amount.split('-')[0];
                            }
                            const inrAmount = parseFloat(amount);
                            if (!isNaN(inrAmount)) {
                              const rubToInrRate = parseFloat(liveExchangeRates.rubToInr) || 1.10;
                              hostelRUB = Math.round(inrAmount / rubToInrRate);
                            }
                          } else if (selectedUniversity.hostel.includes('USD')) {
                            let amount = selectedUniversity.hostel.replace(/[USD$,]/g, '');
                            if (amount.includes('-')) {
                              amount = amount.split('-')[0];
                            }
                            const usdAmount = parseFloat(amount);
                            if (!isNaN(usdAmount)) {
                              hostelRUB = Math.round(usdAmount * 91);
                            }
                          } else {
                            let amount = selectedUniversity.hostel.replace(/[RUB,]/g, '');
                            if (amount.includes('-')) {
                              amount = amount.split('-')[0];
                            }
                            const rubAmount = parseFloat(amount);
                            if (!isNaN(rubAmount)) {
                              hostelRUB = rubAmount;
                            }
                          }
                          
                          const insurance = 7500;
                          const visaExtension = 2000;
                          
                          // Annual total from 2nd year to 6th year (in RUB)
                          const annualRUB = (tuition || 0) + (hostelRUB || 0) + (insurance || 0) + (visaExtension || 0);
                          const rubToInrRate = parseFloat(liveExchangeRates.rubToInr) || 1.10;
                          const annualINR = Math.round(annualRUB * rubToInrRate);
                          
                          // 6-year totals
                          const firstYearINR = parseInt(calculation.totalINR.replace(/[₹,]/g, '')) || 0;
                          const years2to6TotalINR = annualINR * 5; // 5 years (2nd to 6th)
                          const livingExpenses = 180000; // ₹30,000 per year average
                          const totalLivingExpenses = livingExpenses * 6;
                          const total6YearINR = firstYearINR + years2to6TotalINR + totalLivingExpenses;
                          
                          return (
                            <>
                              <div className={`flex justify-between items-center p-3 rounded-xl ${darkMode ? 'bg-gradient-to-r from-green-500/20 to-emerald-500/10 border-green-400/30' : 'bg-gradient-to-r from-green-500/10 to-emerald-500/5 border-green-400/20'} backdrop-blur-sm border shadow-lg`}>
                                <span className={`${darkMode ? 'text-green-300' : 'text-green-600'} font-semibold`}>1st Year (All-Inclusive):</span>
                                <span className={`font-bold text-green-400 text-right whitespace-nowrap`}>{calculation.totalINR}</span>
                              </div>
                              <div className={`rounded-xl ${darkMode ? 'bg-gradient-to-r from-purple-500/20 to-violet-500/10 border-purple-400/30' : 'bg-gradient-to-r from-purple-500/10 to-violet-500/5 border-purple-400/20'} backdrop-blur-sm border shadow-lg p-3`}>
                                <div className="mb-2">
                                  <span className={`${darkMode ? 'text-purple-300' : 'text-purple-600'} font-semibold`}>From 2nd year to 6th year:</span>
                                </div>
                                <div className="space-y-2 ml-4">
                                  <div className={`flex justify-between items-center p-2 rounded-lg ${darkMode ? 'bg-purple-500/10 border-purple-400/20' : 'bg-purple-500/5 border-purple-400/15'} border`}>
                                    <span className={`${darkMode ? 'text-purple-200' : 'text-purple-700'} text-sm font-medium`}>Per year:</span>
                                    <div className="text-right">
                                      <span className={`font-bold ${darkMode ? 'text-purple-300' : 'text-purple-600'} text-sm whitespace-nowrap`}>₹{(annualINR || 0).toLocaleString('en-IN')}</span>
                                      <div className={`text-xs ${darkMode ? 'text-purple-400' : 'text-purple-500'}`}>(per year)</div>
                                    </div>
                                  </div>
                                  {/* Small horizontal line above Total (5 years) */}
                                  <div className={`border-t ${darkMode ? 'border-purple-400/30' : 'border-purple-500/30'} my-2`}></div>
                                  <div className={`flex justify-between items-center p-2 rounded-lg ${darkMode ? 'bg-purple-500/15 border-purple-400/25' : 'bg-purple-500/8 border-purple-400/18'} border`}>
                                    <span className={`${darkMode ? 'text-purple-200' : 'text-purple-700'} text-sm font-medium`}>Total (5 years):</span>
                                    <div className="text-right">
                                      <span className={`font-bold ${darkMode ? 'text-purple-300' : 'text-purple-600'} text-sm whitespace-nowrap`}>₹{(years2to6TotalINR || 0).toLocaleString('en-IN')}</span>
                                      <div className={`text-xs ${darkMode ? 'text-purple-400' : 'text-purple-500'}`}>(₹{(annualINR || 0).toLocaleString('en-IN')} × 5 years)</div>
                                    </div>
                                  </div>
                                </div>
                              </div>
                              <div className={`flex justify-between items-center p-3 rounded-xl ${darkMode ? 'bg-gradient-to-r from-orange-500/20 to-amber-500/10 border-orange-400/30' : 'bg-gradient-to-r from-orange-500/10 to-amber-500/5 border-orange-400/20'} backdrop-blur-sm border shadow-lg`}>
                                <span className={`${darkMode ? 'text-orange-300' : 'text-orange-600'} font-semibold`}>Living expenses (6 years):</span>
                                <div className="text-right">
                                  <span className={`font-bold ${darkMode ? 'text-orange-300' : 'text-orange-600'} text-sm whitespace-nowrap`}>₹{(totalLivingExpenses || 0).toLocaleString('en-IN')}</span>
                                  <div className={`text-xs ${darkMode ? 'text-orange-400' : 'text-orange-500'}`}>(₹15,000 × 12 months × 6 years)</div>
                                </div>
                              </div>
                              {/* Horizontal line above Total 6-Year Investment */}
                              <div className={`border-t-2 ${darkMode ? 'border-blue-400/40' : 'border-blue-500/40'} my-4`}></div>
                              <div className={`flex justify-between items-center p-4 rounded-xl ${darkMode ? 'bg-gradient-to-r from-blue-500/25 to-cyan-500/15 border-blue-400/35' : 'bg-gradient-to-r from-blue-500/15 to-cyan-500/8 border-blue-400/25'} backdrop-blur-sm border shadow-lg`}>
                                <span className={`${darkMode ? 'text-blue-300' : 'text-blue-600'} font-bold`}>Total 6-Year Investment:</span>
                                <div className="text-right">
                                  <span className={`font-bold text-blue-400 text-lg whitespace-nowrap`}>₹{Math.round((total6YearINR || 0)/100000)} Lakhs</span>
                                  <div className={`text-xs ${darkMode ? 'text-blue-400' : 'text-blue-500'}`}>(₹{(total6YearINR || 0).toLocaleString('en-IN')})</div>
                                </div>
                              </div>
                              <div className={`rounded-xl ${darkMode ? 'bg-yellow-500/10 border-yellow-400/30' : 'bg-yellow-500/5 border-yellow-400/20'} border p-3 backdrop-blur-sm`}>
                                <div className="flex items-center gap-2 mb-2">
                                  <svg className="w-4 h-4 text-yellow-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                                  </svg>
                                  <span className="text-yellow-400 text-sm font-semibold">Additional Expenses:</span>
                                </div>
                                <p className={`text-xs ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                                  * Plus return flight tickets (₹65,000) & emergency funds (₹1,00,000)
                                </p>
                              </div>
                            </>
                          );
                        })()}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Additional Information */}
              <div className={`rounded-2xl backdrop-blur-xl ${darkMode ? 'bg-blue-500/10 border-blue-400/30' : 'bg-blue-500/5 border-blue-400/20'} border shadow-2xl overflow-hidden`}>
                <div className="p-3 sm:p-4 lg:p-6">
                  <h3 className="text-base sm:text-lg lg:text-xl font-bold mb-3 sm:mb-4 flex items-center gap-2 bg-gradient-to-r from-blue-400 to-cyan-400 bg-clip-text text-transparent">
                    <div className={`p-1.5 sm:p-2 rounded-lg sm:rounded-xl ${darkMode ? 'bg-blue-500/20 border-blue-400/30' : 'bg-blue-500/10 border-blue-400/20'} backdrop-blur-sm border`}>
                      <svg className="w-4 h-4 sm:w-5 sm:h-5 text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                    </div>
                    Important Notes
                  </h3>
                  <div className="space-y-1.5 sm:space-y-2">
                    <div className={`p-2 sm:p-3 rounded-lg ${darkMode ? 'bg-gray-700/10 border-gray-600/20' : 'bg-gray-50/20 border-gray-200/30'} backdrop-blur-sm border`}>
                      <div className="flex items-start gap-2">
                        <div className="w-1.5 h-1.5 bg-green-400 rounded-full mt-1.5 flex-shrink-0"></div>
                        <div className="min-w-0 flex-1">
                          <div className="text-green-400 font-semibold text-xs sm:text-sm mb-0.5">1st Year includes EVERYTHING:</div>
                          <div className={`${darkMode ? 'text-gray-200' : 'text-gray-700'} text-xs sm:text-sm leading-relaxed`}>
                            Flight tickets (₹65,000), medical, insurance, documentation, visa, OTC charges
                          </div>
                        </div>
                      </div>
                    </div>
                    
                    <div className={`p-2 sm:p-3 rounded-lg ${darkMode ? 'bg-gray-700/10 border-gray-600/20' : 'bg-gray-50/20 border-gray-200/30'} backdrop-blur-sm border`}>
                      <div className="flex items-start gap-2">
                        <div className="w-1.5 h-1.5 bg-purple-400 rounded-full mt-1.5 flex-shrink-0"></div>
                        <div className="min-w-0 flex-1">
                          <div className="text-purple-400 font-semibold text-xs sm:text-sm mb-0.5">From 2nd-6th year:</div>
                          <div className={`${darkMode ? 'text-gray-200' : 'text-gray-700'} text-xs sm:text-sm leading-relaxed`}>
                            Only tuition + hostel + insurance + visa extension (much lower cost)
                          </div>
                        </div>
                      </div>
                    </div>
                    
                    <div className={`p-2 sm:p-3 rounded-lg ${darkMode ? 'bg-gray-700/10 border-gray-600/20' : 'bg-gray-50/20 border-gray-200/30'} backdrop-blur-sm border`}>
                      <div className="flex items-start gap-2">
                        <div className="w-1.5 h-1.5 bg-yellow-400 rounded-full mt-1.5 flex-shrink-0"></div>
                        <div className="min-w-0 flex-1">
                          <div className="text-yellow-400 font-semibold text-xs sm:text-sm mb-0.5">Exchange Rate:</div>
                          <div className={`${darkMode ? 'text-gray-200' : 'text-gray-700'} text-xs sm:text-sm leading-relaxed`}>
                            {liveExchangeRates.isLive ? (
                              <div className="flex items-center gap-1 flex-wrap">
                                <div className="w-1 h-1 bg-green-400 rounded-full animate-pulse"></div>
                                <span>Live: 1 RUB = ₹{liveExchangeRates.rubToInr}</span>
                                <span className="text-xs opacity-75">• Updated: {liveExchangeRates.lastUpdated}</span>
                              </div>
                            ) : (
                              <span>1 RUB = ₹{liveExchangeRates.rubToInr || '1.10'} (approximate)</span>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                    
                    <div className={`p-2 sm:p-3 rounded-lg ${darkMode ? 'bg-gray-700/10 border-gray-600/20' : 'bg-gray-50/20 border-gray-200/30'} backdrop-blur-sm border`}>
                      <div className="flex items-start gap-2">
                        <div className="w-1.5 h-1.5 bg-blue-400 rounded-full mt-1.5 flex-shrink-0"></div>
                        <div className="min-w-0 flex-1">
                          <div className="text-blue-400 font-semibold text-xs sm:text-sm mb-0.5">Total 6-year investment:</div>
                          <div className={`${darkMode ? 'text-gray-200' : 'text-gray-700'} text-xs sm:text-sm leading-relaxed`}>
                            Approximately <span className="font-medium">₹22-28 Lakhs</span> • Monthly living: <span className="font-medium">₹10,000-15,000</span>
                          </div>
                        </div>
                      </div>
                    </div>
                    
                    <div className={`p-2 sm:p-3 rounded-lg ${darkMode ? 'bg-gray-700/10 border-gray-600/20' : 'bg-gray-50/20 border-gray-200/30'} backdrop-blur-sm border`}>
                      <div className="flex items-start gap-2">
                        <div className="w-1.5 h-1.5 bg-orange-400 rounded-full mt-1.5 flex-shrink-0"></div>
                        <div className="min-w-0 flex-1">
                          <div className="text-orange-400 font-semibold text-xs sm:text-sm mb-0.5">Additional costs:</div>
                          <div className={`${darkMode ? 'text-gray-200' : 'text-gray-700'} text-xs sm:text-sm leading-relaxed`}>
                            Return flight: <span className="font-medium">₹50-70k</span> • Biometric: <span className="font-medium">₹2-5k</span> • Emergency fund: <span className="font-medium">₹50k-1L</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Action Buttons */}
              <div className="flex flex-col sm:flex-row gap-4 mt-4 sm:mt-6 pt-3 sm:pt-4 border-t border-gray-200/20">
                <button 
                  onClick={() => {
                    const calculation = calculateTotalFirstYear(selectedUniversity);
                    const message = `Hi! I want admission in ${selectedUniversity.name}. 1st Year Fee: ${calculation.totalINR}. Please guide me about admission process.`;
                    const whatsappUrl = `https://wa.me/916364249519?text=${encodeURIComponent(message)}`;
                    console.log('WhatsApp URL:', whatsappUrl);
                    console.log('Message:', message);
                    window.open(whatsappUrl, '_blank');
                  }}
                  className="flex-1 bg-gradient-to-r from-green-500 to-emerald-500 hover:from-green-600 hover:to-emerald-600 text-white py-4 px-6 rounded-2xl transition-all duration-300 font-bold text-base sm:text-lg flex items-center justify-center gap-3 shadow-xl hover:shadow-2xl hover:scale-105 backdrop-blur-sm border border-green-400/30"
                >
                  <svg className="w-6 h-6 transition-transform duration-300 hover:scale-110" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893A11.821 11.821 0 0020.885 3.488"/>
                  </svg>
                  Get Admission Help (WhatsApp)
                </button>
                <button 
                  onClick={closeModal}
                  className={`px-6 py-4 rounded-2xl border-2 ${darkMode ? 'border-gray-600 hover:border-gray-500 bg-gray-700/50 hover:bg-gray-600/50 text-white' : 'border-gray-300 hover:border-gray-400 bg-gray-100/80 hover:bg-gray-200/80 text-gray-700'} backdrop-blur-sm transition-all duration-300 font-bold text-base sm:text-lg hover:scale-105 shadow-xl`}
                >
                  Close
                </button>
              </div>
            </div>
            </div>
          </div>
        </div>
      )}
      </div>
    </div>
  );
};

export default UniversityFeesPage;